import React, { useState, useEffect, lazy, Suspense } from 'react';
const PredictionMap = lazy(() => import('./PredictionMap'));
import {
  List,
  Datagrid,
  TextField,
  DateField,
  EditButton,
  Edit,
  SimpleForm,
  TextInput,
  DateTimeInput,
  BooleanInput,
  SelectInput,
  Create,
  Show,
  TabbedShowLayout,
  Tab,
  ShowButton,
  useRecordContext,
  useTranslate,
  Button as RaButton,
  TopToolbar,
  useListContext,
  Filter,
  FilterLiveSearch,
  useGetOne,
  useGetList,
  Loading,
  BooleanField,
  useRedirect,
  useDataProvider,
  ListContextProvider,
  useListController,
  NumberField,
  Labeled,
  DatagridConfigurable,
  WrapperField,
  FilterButton,
  SearchInput,
  FilterForm,
  SavedQueriesList,
  FilterList,
  FilterListItem,
  DateInput,
  ReferenceField,
  SimpleShowLayout,
  CreateButton,
  FunctionField,
  useGetMany,
  ReferenceManyField,
  ChipField,
  useNotify,
  useRefresh,
  Confirm,
  ListButton,
  EmailField,
  SelectColumnsButton,
} from 'react-admin';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Chip,
  CardContent,
  Card,
  TextField as MuiTextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CircularProgress,
  StepConnector,
  stepConnectorClasses,
  styled,
  Divider,
  DialogActions,
  Button as MuiButton,
  Grid,
  Link as MuiLink,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { alpha } from '@mui/material/styles';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import Check from '@mui/icons-material/Check';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { BoolChip } from './buyers';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import API_URL from './config';

const StyledSimpleForm = styled(SimpleForm)(({ theme }) => ({
  backgroundColor: '#fff',
  padding: '24px',
  borderRadius: '8px',
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
  '& .MuiFormControl-root': {
    width: '100%',
    maxWidth: '600px',
    marginBottom: '16px',
  },
  '& .MuiInputBase-root': {
    width: '100%',
  },
}));

const DebugComponent = ({ data, label }) => {
  console.log(`Debug ${label}:`, data);
  return null;
};

const HtmlViewer = ({ html }) => {
  const iframeRef = React.useRef(null);

  React.useEffect(() => {
    if (iframeRef.current && html) {
      const iframe = iframeRef.current;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(html);
      iframeDoc.close();
    }
  }, [html]);

  if (!html) {
    return <div>No content available</div>;
  }

  return (
    <iframe
      ref={iframeRef}
      style={{
        width: '100%',
        height: '900px',
        border: '1px solid #ccc',
        borderRadius: '4px',
      }}
      title="HTML Document Viewer"
    />
  );
};

const ExportExcelButton = () => {
  const { filterValues, sort } = useListContext();
  const [loading, setLoading] = useState(false);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const translate = useTranslate();

  const handleExport = async () => {
    setLoading(true);
    try {
      // Build query parameters to match current filters and sorting
      const params = new URLSearchParams();

      // Add filters
      Object.entries(filterValues || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });

      // Add sorting
      if (sort?.field) {
        params.append('_sort', sort.field);
        params.append('_order', sort.order?.toLowerCase() || 'desc');
      }

      // Make direct API call to export endpoint
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await fetch(`${apiUrl}/tenders/export/?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        // Display error message from backend if available
        const errorMessage = result.error || 'Export failed';
        notify(errorMessage, { type: 'error' });
        return;
      }
      
      const data = result.data;

      if (!data || data.length === 0) {
        notify(translate('resources.tenders.export.no_data'), {
          type: 'warning',
        });
        return;
      }
      
      // Display warning if export was limited
      if (result.warning) {
        notify(result.warning, { type: 'warning' });
      }
      
      // Create Excel file
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Tenders');
      XLSX.writeFile(
        workbook,
        `tenders_export_${new Date().toISOString().split('T')[0]}.xlsx`
      );

      notify(
        translate('resources.tenders.export.success', { count: data.length }),
        { type: 'success' }
      );
    } catch (error) {
      console.error('Export error:', error);
      notify(translate('resources.tenders.export.error'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <RaButton
      label={
        loading
          ? translate('resources.tenders.export.exporting')
          : translate('resources.tenders.export.excel')
      }
      onClick={handleExport}
      disabled={loading}
      startIcon={<FileDownloadIcon />}
      sx={{
        backgroundColor: '#E31837',
        color: '#ffffff',
        '&:hover': {
          backgroundColor: '#C41430',
        },
        '& .MuiSvgIcon-root': {
          color: '#ffffff',
        },
      }}
    />
  );
};

const UpdatePreviewModal = ({ open, onClose, updateId, tenderId }) => {
  const translate = useTranslate();
  const { data, isLoading } = useGetOne('tenders', {
    id: `${tenderId}/updates/${updateId}`,
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#fff',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <DialogTitle>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {translate('resources.tenders.fields.update_preview')}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Loading />
        ) : (
          data?.html_body && <HtmlViewer html={data.html_body} />
        )}
      </DialogContent>
    </Dialog>
  );
};

const PreviewButton = (props) => {
  const record = useRecordContext();
  const translate = useTranslate();

  if (!record) return null;

  return (
    <RaButton
      label={translate('resources.tenders.actions.preview')}
      onClick={() => props.onPreview(record.id)}
      startIcon={<VisibilityIcon />}
    />
  );
};

const ProcedureResultField = () => {
  const record = useRecordContext();

  if (!record || !record.procedure_result) return null;

  // Function to format camelCase to Title Case with spaces
  const formatProcedureResult = (text) => {
    // Add space before capital letters in the middle of the string
    const withSpaces = text.replace(/([a-z])([A-Z])/g, '$1 $2');
    // Capitalize first letter and lowercase the rest appropriately
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  };

  // Split by semicolon and remove duplicates
  const values = [
    ...new Set(
      record.procedure_result
        .split(';')
        .map((v) => v.trim())
        .filter((v) => v)
    ),
  ];

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
      {values.map((value, index) => (
        <Chip
          key={index}
          label={formatProcedureResult(value)}
          size="small"
          sx={{
            backgroundColor: '#e3f2fd',
            color: '#1976d2',
            fontWeight: 500,
          }}
        />
      ))}
    </div>
  );
};

const UpdatesDatagrid = () => {
  const translate = useTranslate();
  const [selectedUpdate, setSelectedUpdate] = useState(null);
  const listContext = useListContext();
  const { data, isLoading, resource } = listContext;

  if (isLoading || !data) return null;

  const tenderId = resource.split('/')[1]; // Extract tender ID from resource path

  return (
    <>
      <Datagrid>
        <TextField
          source="notice_type"
          label={translate('resources.tenders.fields.notice_type')}
        />
        <TextField
          source="notice_number"
          label={translate('resources.tenders.fields.notice_number')}
        />
        <TextField
          source="bzp_number"
          label={translate('resources.tenders.fields.bzp_number')}
        />
        <DateField
          source="publication_date"
          showTime
          label={translate('resources.tenders.fields.publication_date')}
        />
        <TextField
          source="order_object"
          label={translate('resources.tenders.fields.order_object')}
        />
        <FunctionField
          label={translate('resources.tenders.fields.procedure_result')}
          render={() => <ProcedureResultField />}
        />
        <DateField
          source="created_at"
          showTime
          label={translate('resources.tenders.fields.created_at')}
        />
        <PreviewButton
          onPreview={(updateId) => {
            if (updateId) {
              setSelectedUpdate(updateId);
            }
          }}
        />
      </Datagrid>

      {selectedUpdate && tenderId && (
        <UpdatePreviewModal
          open={true}
          onClose={() => setSelectedUpdate(null)}
          updateId={selectedUpdate}
          tenderId={tenderId}
        />
      )}
    </>
  );
};

const TenderUpdates = () => {
  const record = useRecordContext();
  const resource = `tenders/${record.id}/updates`;

  const listContext = useListController({
    resource,
    sort: { field: 'publication_date', order: 'DESC' },
    perPage: 10,
    disableSyncWithLocation: true,
  });

  if (listContext.isLoading) return <Loading />;
  if (listContext.error) return <div>Error loading updates</div>;

  return (
    <ListContextProvider value={listContext}>
      <div style={{ marginTop: '1em' }}>
        <UpdatesDatagrid />
      </div>
    </ListContextProvider>
  );
};

const TenderPreview = () => {
  const record = useRecordContext();

  if (!record) {
    return <div>Loading...</div>;
  }

  const htmlContent = record?.details?.raw_data?.htmlBody;

  return <HtmlViewer html={htmlContent} />;
};

const BidderNameLinkField = (props) => {
  const record = useRecordContext(props);
  const translate = useTranslate();
  if (!record) return null;

  const status = record.bidder_status;
  let statusChip = null;

  if (status && status !== 'ACTIVE') {
    let color = 'default';
    let label = status;

    switch (status) {
      case 'BLOCKED':
        color = 'error';
        label = translate('resources.bidders.fields.status_blocked', 'Blocked');
        break;
      case 'DELETED':
        color = 'default';
        label = translate('resources.bidders.fields.status_deleted', 'Deleted');
        break;
      default:
        color = 'default';
        label = status;
    }

    statusChip = (
      <Chip
        label={label}
        color={color}
        size="small"
        style={{ marginLeft: '8px' }}
      />
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <a
        href={`/bidders/${record.bidder}/show`}
        style={{
          color: '#1976d2',
          textDecoration: 'underline',
          cursor: 'pointer',
        }}
      >
        {record.bidder_name}
      </a>
      {statusChip}
    </div>
  );
};

const BidderStatusField = (props) => {
  const record = useRecordContext(props);
  const translate = useTranslate();
  if (!record) return null;

  const chips = [];

  // Winner status chip - only show if winner
  const winnerValue = record.is_winner;
  if (
    winnerValue === true ||
    winnerValue === 'true' ||
    winnerValue === 1 ||
    winnerValue === '1'
  ) {
    chips.push(
      <Chip key="winner" label="Winner" color="success" size="small" />
    );
  }

  // Bidder status chip
  const statusValue = record.status;
  if (statusValue && statusValue !== '') {
    let statusColor = 'default';
    let statusLabel = statusValue;

    switch (statusValue) {
      case 'LEAD':
        statusColor = 'info';
        statusLabel = 'Lead';
        break;
      case 'REJECTED':
        statusColor = 'error';
        statusLabel = 'Rejected';
        break;
      case 'PARTICIPANT':
        statusColor = 'warning';
        statusLabel = 'Participant';
        break;
      default:
        statusColor = 'default';
        statusLabel = statusValue;
    }

    chips.push(
      <Chip key="status" label={statusLabel} color={statusColor} size="small" />
    );
  }

  return (
    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>{chips}</div>
  );
};

const BiddersDatagrid = () => {
  const translate = useTranslate();
  const listContext = useListContext();
  const { data, isLoading } = listContext;

  if (isLoading || !data) return null;

  return (
    <Datagrid>
      <BidderNameLinkField
        label={translate('resources.tenders.fields.bidder_name')}
      />
      <TextField
        source="bidder_tax_number"
        label={translate('resources.bidders.fields.tax_number')}
      />
      <TextField
        source="tender_notice_number"
        label={translate('resources.tenders.fields.tender_notice_number')}
      />
      <BidderStatusField source="status" label="Status" />
      <NumberField
        source="offer_value"
        label={translate('resources.tenders.fields.offer_value')}
        options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
      />
      <DateField
        source="offer_date"
        showTime
        label={translate('resources.tenders.fields.offer_date')}
      />
    </Datagrid>
  );
};

const TenderBidders = () => {
  const record = useRecordContext();
  const resource = `tenders/${record.id}/bidders`;

  const listContext = useListController({
    resource,
    sort: { field: 'offer_date', order: 'DESC' },
    perPage: 1000,
    disableSyncWithLocation: true,
  });

  if (listContext.isLoading) return <Loading />;
  if (listContext.error) return <div>Error loading bidders</div>;

  return (
    <ListContextProvider value={listContext}>
      <div style={{ marginTop: '1em' }}>
        <BiddersDatagrid />
      </div>
    </ListContextProvider>
  );
};

// Converts raw feature values into human-readable reason tags
const buildReasons = (features) => {
  if (!features) return [];
  const reasons = [];

  const cpvExact = features.cpv_exact_match_count || 0;
  const cpv2digit = features.cpv_2digit_match_count || 0;
  const pkdOverlap = features.cpv_pkd_overlap_score || 0;
  const sameOp = features.same_ordering_party_count || 0;
  const sameProvince = features.same_province || 0;
  const sameCity = features.same_city || 0;
  const amountInRange = features.amount_in_range || 0;
  const wins = features.bidder_total_wins || 0;
  const winRate = features.bidder_win_rate || 0;
  const participations = features.bidder_participation_count || 0;

  if (cpvExact > 0)
    reasons.push({ label: `CPV dokładny ×${cpvExact}`, color: '#1b5e20', bg: '#e8f5e9', title: 'Firma brała udział w przetargach z dokładnie tymi samymi kodami CPV' });
  else if (cpv2digit > 0)
    reasons.push({ label: `CPV kategoria ×${cpv2digit}`, color: '#33691e', bg: '#f1f8e9', title: 'Firma brała udział w przetargach z tej samej kategorii CPV (pierwsze 2 cyfry)' });

  if (pkdOverlap > 0)
    reasons.push({ label: `PKD pasuje ${(pkdOverlap * 100).toFixed(0)}%`, color: '#004d40', bg: '#e0f2f1', title: 'Kod PKD firmy pokrywa się z kategorią CPV przetargu' });

  if (sameOp > 0)
    reasons.push({ label: `${sameOp}× ten zamawiający`, color: '#4a148c', bg: '#f3e5f5', title: 'Firma wcześniej składała oferty u tego samego zamawiającego' });

  if (sameCity)
    reasons.push({ label: 'to samo miasto', color: '#bf360c', bg: '#fbe9e7', title: 'Firma pochodzi z tego samego miasta co zamawiający' });
  else if (sameProvince)
    reasons.push({ label: 'to samo województwo', color: '#e65100', bg: '#fff3e0', title: 'Firma pochodzi z tego samego województwa co zamawiający' });

  if (amountInRange)
    reasons.push({ label: 'kwota w zakresie', color: '#0d47a1', bg: '#e3f2fd', title: 'Typowa wartość ofert tej firmy mieści się w przedziale 0.1×–10× wartości przetargu' });

  if (winRate >= 0.3 && participations >= 3)
    reasons.push({ label: `skuteczność ${(winRate * 100).toFixed(0)}%`, color: '#1a237e', bg: '#e8eaf6', title: `Firma wygrała ${wins} z ${participations} przetargów` });
  else if (wins >= 10)
    reasons.push({ label: `${wins} wygranych`, color: '#880e4f', bg: '#fce4ec', title: 'Firma ma dużo wygranych przetargów historycznie' });

  return reasons;
};

const ReasonTags = ({ features }) => {
  const reasons = buildReasons(features);
  if (reasons.length === 0) return <span style={{ color: '#bbb', fontSize: '0.8em' }}>—</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {reasons.map((r, i) => (
        <span
          key={i}
          title={r.title}
          style={{
            background: r.bg,
            color: r.color,
            border: `1px solid ${r.color}33`,
            borderRadius: 12,
            padding: '2px 8px',
            fontSize: '0.75em',
            fontWeight: 600,
            cursor: 'help',
            whiteSpace: 'nowrap',
          }}
        >
          {r.label}
        </span>
      ))}
    </div>
  );
};

const PredictionsDatagrid = ({ correctNips = new Set() }) => {
  const translate = useTranslate();
  const listContext = useListContext();
  const { data } = listContext;
  const dataProvider = useDataProvider();
  const redirect = useRedirect();
  const notify = useNotify();

  if (!data || !Array.isArray(data)) return null;

  const handleRowClick = (id, basePath, record) => {
    if (!record) return;
    const bidderId = record.bidder_id || record.id;
    if (bidderId) {
      redirect('show', 'bidders', bidderId);
    } else {
      notify(
        translate('resources.tenders.errors.no_bidder_id', 'Brak ID oferenta w rekordzie'),
        { type: 'warning' }
      );
    }
  };

  return (
    <Datagrid
      rowClick={handleRowClick}
      rowSx={(record) =>
        correctNips.size > 0 && record.tax_number && correctNips.has(record.tax_number)
          ? { backgroundColor: '#e8f5e9', '&:hover': { backgroundColor: '#c8e6c9 !important' } }
          : {}
      }
    >
      <NumberField
        source="rank"
        label={translate('resources.tenders.fields.prediction_rank', 'Pozycja')}
      />
      <FunctionField
        source="score"
        label="Score"
        render={(record) => {
          const score = record.score || 0;
          let color = 'default';
          if (score >= 0.02) color = 'success';
          else if (score >= 0.01) color = 'info';
          else if (score >= 0.005) color = 'warning';
          return <Chip label={score.toFixed(4)} color={color} size="small" />;
        }}
      />
      <TextField
        source="company_name"
        label={translate('resources.tenders.fields.company_name', 'Nazwa firmy')}
      />
      <FunctionField
        source="features"
        label="Dlaczego?"
        render={(record) => <ReasonTags features={record.features} />}
      />
      <TextField source="city" label={translate('resources.buyers.fields.city')} />
      <TextField source="pkd_code" label={translate('resources.tenders.fields.pkd_code', 'Kod PKD')} />
      <TextField source="tax_number" label={translate('resources.buyers.fields.tax_number')} />
      <TextField source="postal_code" label={translate('resources.buyers.fields.postal_code')} />
    </Datagrid>
  );
};

const StatChip = ({ label, value, color }) => (
  <div style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    background: color || '#f5f5f5', borderRadius: 8,
    padding: '10px 18px', minWidth: 90,
  }}>
    <span style={{ fontSize: '1.25em', fontWeight: 700, color: '#333', lineHeight: 1 }}>{value}</span>
    <span style={{ fontSize: '0.72em', color: '#666', marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
  </div>
);

const PredictionHistoryModal = ({ runs, onClose, onEvaluate, evaluatingId }) => (
  <div style={{
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9999,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }} onClick={onClose}>
    <div style={{
      background: '#fff', borderRadius: 8, padding: '24px 28px', minWidth: 640, maxWidth: 860,
      maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    }} onClick={e => e.stopPropagation()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <strong style={{ fontSize: '1.05em' }}>Historia generowań predykcji</strong>
        <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '1.2em', color: '#666' }}>✕</button>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85em' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #eee' }}>
            <th style={{ textAlign: 'left', padding: '6px 8px', color: '#888', fontWeight: 600 }}>Data</th>
            <th style={{ textAlign: 'center', padding: '6px 8px', color: '#888', fontWeight: 600 }}>Kandydaci</th>
            <th style={{ textAlign: 'center', padding: '6px 8px', color: '#888', fontWeight: 600 }}>Predykcje</th>
            <th style={{ textAlign: 'center', padding: '6px 8px', color: '#888', fontWeight: 600 }}>Precyzja</th>
            <th style={{ textAlign: 'center', padding: '6px 8px', color: '#888', fontWeight: 600 }}>Pokrycie</th>
            <th style={{ textAlign: 'center', padding: '6px 8px', color: '#888', fontWeight: 600 }}>Akcja</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((r, i) => (
            <tr key={r.id} style={{ borderBottom: '1px solid #f0f0f0', background: i === 0 ? '#fafff8' : 'transparent' }}>
              <td style={{ padding: '7px 8px' }}>
                {new Date(r.generated_at).toLocaleString('pl-PL')}
                {i === 0 && <span style={{ marginLeft: 6, fontSize: '0.8em', color: '#2e7d32', fontWeight: 600 }}>najnowszy</span>}
              </td>
              <td style={{ textAlign: 'center', padding: '7px 8px' }}>{r.relevant_candidates ?? '—'}</td>
              <td style={{ textAlign: 'center', padding: '7px 8px' }}>{(r.predictions || []).length}</td>
              <td style={{ textAlign: 'center', padding: '7px 8px' }}>
                {r.precision != null
                  ? <span style={{ color: '#2e7d32', fontWeight: 600 }}>{(r.precision * 100).toFixed(0)}%</span>
                  : <span style={{ color: '#bbb' }}>—</span>}
              </td>
              <td style={{ textAlign: 'center', padding: '7px 8px' }}>
                {r.recall != null
                  ? <span style={{ color: '#1565c0', fontWeight: 600 }}>{(r.recall * 100).toFixed(0)}%</span>
                  : <span style={{ color: '#bbb' }}>—</span>}
              </td>
              <td style={{ textAlign: 'center', padding: '7px 8px' }}>
                {r.evaluated_at && r.actual_bidders_count > 0 ? (
                  <span style={{ color: '#aaa', fontSize: '0.8em' }}>oceniony</span>
                ) : (
                  <button
                    disabled={evaluatingId === r.id}
                    onClick={() => onEvaluate(r.id)}
                    style={{
                      background: evaluatingId === r.id ? '#eee' : '#1565c0',
                      color: evaluatingId === r.id ? '#999' : '#fff',
                      border: 'none', borderRadius: 4, padding: '3px 10px',
                      cursor: evaluatingId === r.id ? 'not-allowed' : 'pointer',
                      fontSize: '0.8em', fontWeight: 600,
                    }}
                    title={r.evaluated_at && r.actual_bidders_count === 0 ? 'Oceniono, ale brak oferentów — oceń ponownie gdy zostaną pobrani' : ''}
                  >
                    {evaluatingId === r.id ? '…' : r.evaluated_at && r.actual_bidders_count === 0 ? 'Oceń ponownie' : 'Oceń'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ marginTop: 12, fontSize: '0.75em', color: '#999' }}>
        * Ewaluacja pomija oferentów dodanych do systemu po dacie generowania predykcji — model nie mógł ich przewidzieć.
      </p>
    </div>
  </div>
);

const parseCpvCategories = (cpvStr) => {
  if (!cpvStr) return [];
  const seen4 = new Map();
  for (const part of cpvStr.split(',')) {
    const m = part.trim().match(/^(\d{8})(?:-\d+)?\s*\((.+?)\)/);
    if (m) {
      const prefix4 = m[1].slice(0, 4);
      if (!seen4.has(prefix4)) {
        seen4.set(prefix4, m[2].replace(/\u00a0/g, ' ').trim());
      }
    }
  }
  return Array.from(seen4.entries()).map(([code, name]) => ({ code, name }));
};

const TenderPredictions = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const [run, setRun] = useState(null);
  const [allRuns, setAllRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [evaluatingId, setEvaluatingId] = useState(null);
  const [error, setError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const authHeaders = () => {
    const token = localStorage.getItem('access_token');
    const h = { Accept: 'application/json', 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  const loadRuns = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${apiUrl}/tenders/${record.id}/prediction-runs/`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const runs = await res.json();
      setAllRuns(runs);
      setRun(runs.length > 0 ? runs[0] : null);
      setError(null);
    } catch (err) {
      setError(err.message || 'Error loading predictions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (record?.id) loadRuns();
  }, [record?.id]);


  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const res = await fetch(`${apiUrl}/tenders/${record.id}/generate-predictions/`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const newRun = data.run;
      setRun(newRun);
      setAllRuns(prev => [newRun, ...prev]);
      notify('Predykcje wygenerowane', { type: 'success' });
    } catch (err) {
      notify(`Błąd: ${err.message}`, { type: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  const handleEvaluate = async (runId) => {
    try {
      setEvaluatingId(runId);
      const res = await fetch(
        `${apiUrl}/tenders/${record.id}/prediction-runs/${runId}/evaluate/`,
        { method: 'POST', headers: authHeaders() }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const updatedRun = data.run;
      setAllRuns(prev => prev.map(r => r.id === updatedRun.id ? updatedRun : r));
      if (run && run.id === updatedRun.id) setRun(updatedRun);
      if (data.warning === 'no_bidders') {
        notify('Brak oferentów — przetarg nie ma jeszcze pobranych oferentów. Oceń ponownie gdy będą dostępni.', { type: 'warning' });
      } else {
        notify('Ewaluacja zakończona', { type: 'success' });
      }
    } catch (err) {
      notify(`Błąd ewaluacji: ${err.message}`, { type: 'error' });
    } finally {
      setEvaluatingId(null);
    }
  };

  if (loading) return <Loading />;

  const predictions = run
    ? (run.predictions || []).map((item, index) => ({
        ...item,
        id: item.bidder_id ?? index,
        rank: item.rank ?? index + 1,
        company_name: item.company_name ?? item.bidder_name ?? '',
        pkd_code: item.pkd_code ?? item.pkd ?? '',
      }))
    : [];

  const correctNips = new Set(run?.evaluation_details?.correct_tax_numbers || []);

  const signals = (() => {
    if (!predictions.length) return null;
    const f = (p) => p.features || {};
    const scores = predictions.map(p => p.score);
    const scoreSpread = scores.length >= 2 ? (Math.max(...scores) - Math.min(...scores)) : 0;
    const exactCpv     = predictions.filter(p => (f(p).cpv_exact_match_count ?? 0) > 0).length;
    const knowsBuyer   = predictions.filter(p => f(p).same_ordering_party_count > 0).length;
    const sameProvince = predictions.filter(p => f(p).same_province === 1).length;
    const recentlyActive = predictions.filter(p => f(p).participation_last_12m > 0).length;
    const coldStart    = predictions.filter(p => (f(p).bidder_participation_count ?? 99) <= 3).length;
    const noPreciseCpv = predictions.every(p => (f(p).cpv_4digit_match_count ?? 0) === 0);

    // Score distribution across full top-50
    const above50 = scores.filter(s => s >= 0.5).length;
    const above20 = scores.filter(s => s >= 0.2).length;
    // Cliff: biggest single drop in scores between consecutive positions
    let cliffAfter = null;
    let cliffDrop = 0;
    for (let i = 0; i < scores.length - 1; i++) {
      const drop = scores[i] - scores[i + 1];
      if (drop > cliffDrop) { cliffDrop = drop; cliffAfter = i + 1; }
    }

    return { scoreSpread, exactCpv, knowsBuyer, sameProvince, recentlyActive, coldStart, noPreciseCpv, above50, above20, cliffAfter, cliffDrop };
  })();

  const predictionsTop10 = predictions.slice(0, 10);

  const listContextValue = {
    data: predictionsTop10,
    total: predictionsTop10.length,
    isLoading: false,
    error: null,
    resource: 'predictions',
    sort: { field: 'rank', order: 'ASC' },
    filter: {},
    perPage: 100,
    page: 1,
  };

  const btnStyle = (color, disabled) => ({
    background: disabled ? '#ccc' : color,
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    padding: '7px 16px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontWeight: 600,
    fontSize: '0.85em',
    letterSpacing: '0.02em',
    whiteSpace: 'nowrap',
  });

  const pctColor = (v) => v == null ? '#888' : v >= 0.5 ? '#2e7d32' : v >= 0.25 ? '#e65100' : '#c62828';
  const pctBg   = (v) => v == null ? '#f5f5f5' : v >= 0.5 ? '#e8f5e9' : v >= 0.25 ? '#fff3e0' : '#ffebee';

  return (
    <div style={{ marginTop: '1em' }}>

      {/* Dashboard header card */}
      <div style={{
        background: '#fff',
        border: '1px solid #e8e8e8',
        borderRadius: 10,
        padding: '16px 20px',
        marginBottom: '1.25em',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
          {/* Left: stats chips */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {run ? (
              <>
                <StatChip label="Predykcje" value={predictionsTop10.length} color="#e8f5e9" />
                <StatChip label="Kandydaci" value={run.relevant_candidates ?? '—'} color="#e3f2fd" />
                {run.evaluated_at && run.precision != null && (() => {
                  const correctNipSet = new Set(run.evaluation_details?.correct_tax_numbers || []);
                  const top10correct = predictionsTop10.filter(p => p.tax_number && correctNipSet.has(p.tax_number)).length;
                  const top10total = predictionsTop10.length;
                  const pct = top10total ? Math.round(top10correct / top10total * 100) : 0;
                  return (
                    <StatChip
                      label="Precyzja"
                      value={`${top10correct}/${top10total} (${pct}%)`}
                      color="#fff8e1"
                    />
                  );
                })()}
                {run.evaluated_at && run.recall != null && (
                  <StatChip
                    label="Pokrycie"
                    value={`${run.correct_count}/${run.actual_bidders_count} (${(run.recall * 100).toFixed(0)}%)`}
                    color="#e8eaf6"
                  />
                )}
                {run.evaluated_at && run.excluded_new_bidders_count > 0 && (
                  <StatChip label="Pominięci (nowi)" value={run.excluded_new_bidders_count} color="#fce4ec" />
                )}
              </>
            ) : (
              <span style={{ color: '#999', fontSize: '0.9em', alignSelf: 'center' }}>Brak wygenerowanych predykcji</span>
            )}
          </div>

          {/* Right: action buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {run && (!run.evaluated_at || (run.actual_bidders_count === 0 && !run.excluded_new_bidders_count)) && (
                <button
                  onClick={() => handleEvaluate(run.id)}
                  disabled={evaluatingId === run.id}
                  style={btnStyle('#1565c0', evaluatingId === run.id)}
                >
                  {evaluatingId === run.id ? 'Ocenianie…' : 'Oceń trafność'}
                </button>
              )}
              <button
                onClick={handleGenerate}
                disabled={generating}
                style={btnStyle('#c62828', generating)}
              >
                {generating ? 'Generowanie…' : run ? 'Generuj ponownie' : 'Generuj predykcje'}
              </button>
            </div>
            {allRuns.length > 0 && (
              <button
                onClick={() => setShowHistory(true)}
                style={{
                  background: 'none', border: 'none', color: '#1565c0',
                  cursor: 'pointer', fontSize: '0.8em', padding: '2px 0',
                  textDecoration: 'underline',
                }}
              >
                Historia ({allRuns.length} {allRuns.length === 1 ? 'generowanie' : allRuns.length < 5 ? 'generowania' : 'generowań'})
              </button>
            )}
          </div>
        </div>

        {/* Signals row */}
        {signals && (
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #f0f0f0', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '0.75em', color: '#aaa', marginRight: 4 }}>Sygnały:</span>
            {signals.exactCpv > 0 && (
              <span title="Firmy które historycznie startowały w przetargach z dokładnie tym samym kodem CPV" style={{ background: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9', borderRadius: 20, padding: '2px 10px', fontSize: '0.78em', fontWeight: 600 }}>
                🎯 {signals.exactCpv} z dokładnym CPV
              </span>
            )}
            {signals.scoreSpread < 0.005 && (
              <span title={`Wszystkie firmy mają bardzo podobny score (rozpiętość ${(signals.scoreSpread * 100).toFixed(2)}%) — model nie wyróżnia wyraźnych liderów`} style={{ background: '#fafafa', color: '#888', border: '1px solid #e0e0e0', borderRadius: 20, padding: '2px 10px', fontSize: '0.78em', fontWeight: 600 }}>
                ≈ Wyrównane pole
              </span>
            )}
            {signals.knowsBuyer > 0 && (
              <span title="Firmy które już wcześniej startowały u tego zamawiającego" style={{ background: '#e8f5e9', color: '#1b5e20', border: '1px solid #c8e6c9', borderRadius: 20, padding: '2px 10px', fontSize: '0.78em', fontWeight: 600 }}>
                🤝 {signals.knowsBuyer} zna zamawiającego
              </span>
            )}
            {signals.sameProvince > 0 && (
              <span title="Firmy z tego samego województwa co zamawiający" style={{ background: '#e3f2fd', color: '#1565c0', border: '1px solid #bbdefb', borderRadius: 20, padding: '2px 10px', fontSize: '0.78em', fontWeight: 600 }}>
                📍 {signals.sameProvince} z regionu
              </span>
            )}
            {signals.recentlyActive > 0 && (
              <span title="Firmy aktywne w przetargach w ciągu ostatnich 12 miesięcy" style={{ background: '#f3e5f5', color: '#6a1b9a', border: '1px solid #e1bee7', borderRadius: 20, padding: '2px 10px', fontSize: '0.78em', fontWeight: 600 }}>
                ⚡ {signals.recentlyActive} aktywnych w 12m
              </span>
            )}
            {signals.coldStart >= 3 && (
              <span title="Firmy z bardzo małą historią — model może słabiej je oceniać" style={{ background: '#fff3e0', color: '#e65100', border: '1px solid #ffe0b2', borderRadius: 20, padding: '2px 10px', fontSize: '0.78em', fontWeight: 600 }}>
                ❄️ {signals.coldStart} bez historii
              </span>
            )}
            {signals.noPreciseCpv && (
              <span title="Żadna przewidziana firma nie ma precyzyjnego dopasowania CPV 4-cyfrowego — wyniki mogą być mało trafne" style={{ background: '#fafafa', color: '#888', border: '1px solid #e0e0e0', borderRadius: 20, padding: '2px 10px', fontSize: '0.78em', fontWeight: 600 }}>
                🔍 Tylko ogólne CPV
              </span>
            )}
            {signals.above50 > 0 && (
              <span title={`${signals.above50} firm ma score ≥ 0.5 — model jest względnie pewny co do tych kandydatów`} style={{ background: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9', borderRadius: 20, padding: '2px 10px', fontSize: '0.78em', fontWeight: 600 }}>
                📈 {signals.above50} z score ≥ 50%
              </span>
            )}
            {signals.above50 === 0 && signals.above20 > 0 && (
              <span title={`Żadna firma nie przekroczyła 0.5, ale ${signals.above20} ma score ≥ 0.2 — przetarg trudny do przewidzenia`} style={{ background: '#fff3e0', color: '#e65100', border: '1px solid #ffe0b2', borderRadius: 20, padding: '2px 10px', fontSize: '0.78em', fontWeight: 600 }}>
                📉 Tylko {signals.above20} z score ≥ 20%
              </span>
            )}
            {signals.cliffAfter !== null && signals.cliffDrop >= 0.2 && (
              <span title={`Duży skok w score po pozycji ${signals.cliffAfter} (spadek o ${(signals.cliffDrop * 100).toFixed(0)}%) — model wyraźnie preferuje top-${signals.cliffAfter}`} style={{ background: '#e3f2fd', color: '#1565c0', border: '1px solid #bbdefb', borderRadius: 20, padding: '2px 10px', fontSize: '0.78em', fontWeight: 600 }}>
                ✂️ Klif po #{signals.cliffAfter}
              </span>
            )}
          </div>
        )}

        {/* CPV categories */}
        {(() => {
          const cats = parseCpvCategories(record?.details?.cpv_code);
          if (!cats.length) return null;
          return (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f0f0f0', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: '0.75em', color: '#aaa', marginRight: 2 }}>CPV:</span>
              {cats.map(({ code, name }) => (
                <span key={code} title={`CPV ${code}xxxx`} style={{ background: '#f3f4f6', color: '#444', border: '1px solid #e5e7eb', borderRadius: 4, padding: '2px 8px', fontSize: '0.76em', fontFamily: 'monospace' }}>
                  <span style={{ color: '#888', marginRight: 4 }}>{code}</span>{name}
                </span>
              ))}
            </div>
          );
        })()}

        {/* Bottom meta row */}
        {run && (
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #f0f0f0', display: 'flex', gap: '1.5em', flexWrap: 'wrap', fontSize: '0.78em', color: '#888' }}>
            <span>Wygenerowano: <strong style={{ color: '#555' }}>{new Date(run.generated_at).toLocaleString('pl-PL')}</strong></span>
            {run.model_version && (
              <span>Wersja modelu: <strong style={{ color: '#555' }}>{run.model_version.replace('T', ' ').slice(0, 19)}</strong></span>
            )}
            {run.evaluated_at && run.actual_bidders_count > 0 ? (
              <span style={{ color: '#2e7d32' }}>
                ✓ Oceniono {new Date(run.evaluated_at).toLocaleDateString('pl-PL')}
                {run.excluded_new_bidders_count > 0 && ` · ${run.excluded_new_bidders_count} oferentów pominięto (za nowi)`}
              </span>
            ) : run.evaluated_at && run.actual_bidders_count === 0 ? (
              <span style={{ color: '#e65100' }}>⚠ Brak oferentów — oceniono {new Date(run.evaluated_at).toLocaleDateString('pl-PL')}, ale przetarg nie ma jeszcze oferentów. Oceń ponownie gdy zostaną pobrani.</span>
            ) : (
              <span style={{ color: '#e65100' }}>Brak ewaluacji — kliknij „Oceń trafność" po pobraniu oferentów</span>
            )}
          </div>
        )}
      </div>

      {error && (
        <div style={{ padding: '1em', color: '#c62828', background: '#ffebee', borderRadius: 6, marginBottom: '1em' }}>
          Błąd: {error}
        </div>
      )}

      {predictions.length > 0 && (
        <Suspense fallback={null}>
          <PredictionMap run={run} orderingParty={record?.ordering_party} />
        </Suspense>
      )}

      {predictions.length > 0 && (
        <ListContextProvider value={listContextValue}>
          <PredictionsDatagrid correctNips={correctNips} />
        </ListContextProvider>
      )}

      {showHistory && (
        <PredictionHistoryModal
          runs={allRuns}
          onClose={() => setShowHistory(false)}
          onEvaluate={handleEvaluate}
          evaluatingId={evaluatingId}
        />
      )}
    </div>
  );
};

export const TenderUpdateShow = () => {
  const translate = useTranslate();
  const record = useRecordContext();
  const redirect = useRedirect();

  if (!record?.html_body) return null;

  return (
    <div>
      <TopToolbar>
        <RaButton
          label={translate('ra.action.back')}
          onClick={() => redirect('show', 'tenders', record.tender_id)}
        />
      </TopToolbar>
      <div style={{ padding: '20px' }}>
        <HtmlViewer html={record.html_body} />
      </div>
    </div>
  );
};

const TenderTitle = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  return <div>{record?.title}</div>;
};

const CustomConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.primary.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.success.main,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.grey[400],
    borderLeftWidth: 2,
    minHeight: 20,
    marginLeft: 11, // align with icon center
  },
}));

const CustomStepIconRoot = styled('div')(({ theme, ownerState }) => ({
  color: theme.palette.grey[400],
  display: 'flex',
  height: 22,
  alignItems: 'center',
  ...(ownerState.active && {
    color: theme.palette.primary.main,
  }),
  '& .CustomStepIcon-completedIcon': {
    color: theme.palette.success.main,
    zIndex: 1,
    fontSize: 24,
  },
  '& .CustomStepIcon-errorIcon': {
    color: theme.palette.error.main,
    zIndex: 1,
    fontSize: 24,
  },
  '& .CustomStepIcon-circle': {
    width: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: 'currentColor',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#fff',
    fontSize: '0.75rem',
  },
}));

function CustomStepIcon(props) {
  const { active, completed, className, icon, error } = props;

  if (error) {
    return (
      <CustomStepIconRoot
        ownerState={{ active: true, error: true }}
        className={className}
      >
        <ErrorOutline className="CustomStepIcon-errorIcon" />
      </CustomStepIconRoot>
    );
  }

  if (completed) {
    return (
      <CustomStepIconRoot
        ownerState={{ completed: true }}
        className={className}
      >
        <Check className="CustomStepIcon-completedIcon" />
      </CustomStepIconRoot>
    );
  }

  return (
    <CustomStepIconRoot ownerState={{ active }} className={className}>
      <div className="CustomStepIcon-circle">{String(icon)}</div>
    </CustomStepIconRoot>
  );
}

const collapseRepeatedMessages = (messages) => {
  const result = [];
  for (const msg of messages) {
    if (result.length > 0 && result[result.length - 1].msg === msg) {
      result[result.length - 1].count++;
    } else {
      result.push({ msg, count: 1 });
    }
  }
  return result;
};

const WorkflowProgress = () => {
  const record = useRecordContext();
  const [workflowData, setWorkflowData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [blockToRestart, setBlockToRestart] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [processingBidders, setProcessingBidders] = useState(false);
  const [bidderRestartData, setBidderRestartData] = useState(null);
  const [winnerRestartData, setWinnerRestartData] = useState(null);
  const [asyncDataDialog, setAsyncDataDialog] = useState(null);
  const [searchParams] = useSearchParams();

  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();

  const handleRestartClick = (blockName) => {
    setBlockToRestart(blockName);
    setConfirmOpen(true);
  };

  const handleBidderRestartClick = (bidderWorkflowId, blockName) => {
    setBidderRestartData({ bidderWorkflowId, blockName });
    setConfirmOpen(true);
  };

  const handleWinnerRestartClick = (blockName) => {
    setWinnerRestartData({ blockName });
    setConfirmOpen(true);
  };

  const handleConfirmRestart = () => {
    if (bidderRestartData) {
      // Handle bidder workflow restart
      const { bidderWorkflowId, blockName } = bidderRestartData;
      const token = localStorage.getItem('access_token');
      fetch(`${API_URL}/tenders/${record.id}/workflow/restart-bidder/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          bidder_workflow_id: bidderWorkflowId,
          block_name: blockName,
        }),
      })
        .then((response) => {
          if (!response.ok) {
            return response.json().then((err) => {
              throw new Error(
                err.detail || 'Failed to restart bidder workflow'
              );
            });
          }
          return response.json();
        })
        .then(() => {
          notify('Bidder workflow restarted successfully.', {
            type: 'success',
          });
          refresh();
          setRefreshTrigger((prev) => prev + 1);
        })
        .catch((error) => {
          notify(`Error: ${error.message}`, { type: 'error' });
        })
        .finally(() => {
          setConfirmOpen(false);
          setBidderRestartData(null);
        });
    } else if (winnerRestartData) {
      // Handle winner workflow restart
      const { blockName } = winnerRestartData;
      const token = localStorage.getItem('access_token');
      fetch(`${API_URL}/tenders/${record.id}/workflow/restart-winner/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ block_name: blockName }),
      })
        .then((response) => {
          if (!response.ok) {
            return response.json().then((err) => {
              throw new Error(
                err.detail || 'Failed to restart winner workflow'
              );
            });
          }
          return response.json();
        })
        .then(() => {
          notify('Winner workflow restarted successfully.', {
            type: 'success',
          });
          refresh();
          setRefreshTrigger((prev) => prev + 1);
        })
        .catch((error) => {
          notify(`Error: ${error.message}`, { type: 'error' });
        })
        .finally(() => {
          setConfirmOpen(false);
          setWinnerRestartData(null);
        });
    } else if (blockToRestart) {
      // Handle tender workflow restart (existing logic)
      const token = localStorage.getItem('access_token');
      fetch(`${API_URL}/tenders/${record.id}/workflow/restart/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ block_name: blockToRestart }),
      })
        .then((response) => {
          if (!response.ok) {
            return response.json().then((err) => {
              throw new Error(err.detail || 'Failed to restart workflow');
            });
          }
          return response.json();
        })
        .then(() => {
          notify('Workflow restarted successfully.', { type: 'success' });
          refresh();
          setRefreshTrigger((prev) => prev + 1); // Trigger re-fetch of workflow data
        })
        .catch((error) => {
          notify(`Error: ${error.message}`, { type: 'error' });
        })
        .finally(() => {
          setConfirmOpen(false);
          setBlockToRestart(null);
        });
    }
  };

  const handleProcessWorkflow = () => {
    if (!record?.id) return;

    setProcessing(true);
    const token = localStorage.getItem('access_token');
    fetch(`${API_URL}/tenders/${record.id}/workflow/process/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((err) => {
            throw new Error(err.detail || 'Failed to process workflow');
          });
        }
        return response.json();
      })
      .then(() => {
        notify('Workflow processed successfully.', { type: 'success' });
        refresh();
        setRefreshTrigger((prev) => prev + 1); // Trigger re-fetch of workflow data
      })
      .catch((error) => {
        notify(`Error: ${error.message}`, { type: 'error' });
      })
      .finally(() => {
        setProcessing(false);
      });
  };

  const handleProcessBidderWorkflows = () => {
    if (!record?.id) return;

    setProcessingBidders(true);
    const token = localStorage.getItem('access_token');
    fetch(`${API_URL}/tenders/${record.id}/workflow/process-bidders/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ limit: 10 }),
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((err) => {
            throw new Error(err.detail || 'Failed to process bidder workflows');
          });
        }
        return response.json();
      })
      .then(() => {
        notify('Bidder workflows processed successfully.', { type: 'success' });
        refresh();
        setRefreshTrigger((prev) => prev + 1); // Trigger re-fetch of workflow data
      })
      .catch((error) => {
        notify(`Error: ${error.message}`, { type: 'error' });
      })
      .finally(() => {
        setProcessingBidders(false);
      });
  };

  const handleProcessBidderWorkflow = (bidderOfferId) => {
    if (!record?.id) return;

    const token = localStorage.getItem('access_token');
    fetch(`${API_URL}/tenders/${record.id}/workflow/process-bidder/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ bidder_offer_id: bidderOfferId }),
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((err) => {
            throw new Error(err.detail || 'Failed to process bidder workflow');
          });
        }
        return response.json();
      })
      .then(() => {
        notify('Bidder workflow processed successfully.', { type: 'success' });
        refresh();
        setRefreshTrigger((prev) => prev + 1); // Trigger re-fetch of workflow data
      })
      .catch((error) => {
        notify(`Error: ${error.message}`, { type: 'error' });
      });
  };

  const handleProcessWinnerWorkflow = () => {
    if (!record?.id) return;

    setProcessing(true);
    const token = localStorage.getItem('access_token');
    fetch(`${API_URL}/tenders/${record.id}/workflow/process-winner/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((err) => {
            throw new Error(err.detail || 'Failed to process winner workflow');
          });
        }
        return response.json();
      })
      .then(() => {
        notify('Winner workflow processed successfully.', { type: 'success' });
        refresh();
        setRefreshTrigger((prev) => prev + 1); // Trigger re-fetch of workflow data
      })
      .catch((error) => {
        notify(`Error: ${error.message}`, { type: 'error' });
      })
      .finally(() => {
        setProcessing(false);
      });
  };

  const handleArchiveBidderWorkflow = (bidderWorkflowId) => {
    if (!record?.id) return;

    const token = localStorage.getItem('access_token');
    fetch(`${API_URL}/tenders/${record.id}/workflow/archive/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        workflow_type: 'bidder',
        bidder_workflow_id: bidderWorkflowId,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((err) => {
            throw new Error(err.detail || 'Failed to archive bidder workflow');
          });
        }
        return response.json();
      })
      .then(() => {
        notify('Bidder workflow archived successfully.', { type: 'success' });
        refresh();
        setRefreshTrigger((prev) => prev + 1);
      })
      .catch((error) => {
        notify(`Error: ${error.message}`, { type: 'error' });
      });
  };

  useEffect(() => {
    if (record && record.id) {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      fetch(`${API_URL}/tenders/${record.id}/workflow/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((response) => {
          if (!response.ok) throw new Error('Network response was not ok');
          return response.json();
        })
        .then((data) => {
          setWorkflowData(data);
          setLoading(false);
        })
        .catch((error) => {
          setError(error);
          setLoading(false);
        });
    }
  }, [record, refreshTrigger]); // Added refreshTrigger to dependencies

  // Scroll to specific block if URL params are present
  useEffect(() => {
    if (!workflowData || loading) return;

    const bidderWorkflowId = searchParams.get('bidderWorkflow');
    const blockName = searchParams.get('block');

    if (bidderWorkflowId && blockName) {
      // Wait a bit for DOM to render
      setTimeout(() => {
        const blockId = `bidder-workflow-${bidderWorkflowId}-block-${blockName
          .replace(/\s+/g, '-')
          .toLowerCase()}`;
        const element = document.getElementById(blockId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Highlight the block briefly
          element.style.transition = 'box-shadow 0.3s ease';
          element.style.boxShadow = '0 0 0 4px rgba(244, 67, 54, 0.3)';
          setTimeout(() => {
            element.style.boxShadow = '';
          }, 2000);
        }
      }, 500);
    }
  }, [workflowData, loading, searchParams]);

  if (loading) return <CircularProgress />;
  if (error)
    return (
      <Typography color="error">
        Error loading workflow: {error.message}
      </Typography>
    );
  if (!workflowData)
    return <Typography>Could not load workflow data.</Typography>;

  const {
    workflow_state,
    all_blocks,
    bidder_workflows,
    bidder_workflow_blocks,
    winner_workflow,
    winner_workflow_blocks,
  } = workflowData;

  // Find the last executed step (done or failed)
  const executedSteps = workflow_state?.steps || [];
  const lastExecutedIndex =
    executedSteps.length > 0
      ? all_blocks.findIndex(
          (b) => b === executedSteps[executedSteps.length - 1].block_name
        )
      : -1;
  const failedStepIndex = executedSteps.findIndex((s) => s.status === 'failed');
  const failedBlockName =
    failedStepIndex !== -1 ? executedSteps[failedStepIndex].block_name : null;
  const failedErrorMessage =
    failedStepIndex !== -1
      ? executedSteps[failedStepIndex].error_message
      : null;

  // Helper for status color/icon
  const statusIcon = (status) => {
    if (status === 'done')
      return <CheckCircleIcon color="success" sx={{ fontSize: 20, mr: 1 }} />;
    if (status === 'failed')
      return <ErrorOutline color="error" sx={{ fontSize: 20, mr: 1 }} />;
    if (status === 'running')
      return <CircularProgress size={18} sx={{ mr: 1 }} />;
    if (status === 'waiting')
      return (
        <RadioButtonUncheckedIcon
          color="warning"
          sx={{ fontSize: 20, mr: 1 }}
        />
      );
    return (
      <RadioButtonUncheckedIcon
        sx={{ fontSize: 20, mr: 1, color: 'grey.400' }}
      />
    );
  };

  // Helper for status color and label
  const statusColor = (status) => {
    switch (status) {
      case 'done':
        return 'success';
      case 'running':
        return 'primary';
      case 'failed':
        return 'error';
      case 'waiting':
        return 'warning';
      case 'archived':
        return 'default';
      default:
        // Check if tender is cancelled
        if (record?.status === 'cancelled') return 'error';
        return 'default';
    }
  };

  const statusLabel = (status) => {
    switch (status) {
      case 'done':
        return 'Done';
      case 'running':
        return 'Running';
      case 'failed':
        return 'Failed';
      case 'waiting':
        return 'Waiting';
      case 'archived':
        return 'Archived';
      default:
        // Check if tender is cancelled
        if (record?.status === 'cancelled') return 'Cancelled';
        return 'Pending';
    }
  };

  // Build a unified list: for each block, attach step history if exists
  const unifiedSteps = all_blocks.map((block, idx) => {
    const step = executedSteps.find((s) => s.block_name === block);
    return {
      block_name: block,
      ...step,
      isExecuted: !!step,
      isFailed: step?.status === 'failed',
      isDone: step?.status === 'done',
      isPending: !step,
      canRestart: !!step && record?.status !== 'cancelled',
    };
  });

  return (
    <Box sx={{ maxWidth: 960, mt: 2 }}>
      {/* Tender Workflow Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2,
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 1,
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ mr: 2 }}>
              Tender Workflow Status:
            </Typography>
            <Chip
              label={statusLabel(workflow_state?.status)}
              color={statusColor(workflow_state?.status)}
              sx={{
                fontWeight: 600,
                fontSize: 14,
                height: 28,
                minWidth: 100,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            />
            {workflow_state?.next_run_at && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                Następne uruchomienie:{' '}
                {new Date(workflow_state.next_run_at).toLocaleString('pl-PL', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </Typography>
            )}
          </Box>
        </Box>
        <MuiButton
          variant="contained"
          color="primary"
          onClick={handleProcessWorkflow}
          disabled={
            processing ||
            workflow_state?.status === 'done' ||
            workflow_state?.status === 'failed' ||
            workflow_state?.status === 'archived' ||
            record?.is_archived ||
            record?.status === 'cancelled' ||
            (workflow_state?.next_run_at &&
              new Date(workflow_state.next_run_at) > new Date())
          }
          startIcon={
            processing ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <PlayArrowIcon />
            )
          }
        >
          Process Next Step
        </MuiButton>
      </Box>
      <Box>
        {unifiedSteps.map((step, idx) => (
          <Box
            key={step.block_name}
            sx={{
              mb: 2,
              p: 0,
              border: '1.5px solid',
              borderRadius: 2,
              bgcolor: 'transparent',
              borderColor: step.isFailed
                ? 'error.main'
                : step.isDone
                ? 'success.light'
                : 'grey.300',
              color: step.isFailed
                ? 'error.main'
                : step.isDone
                ? 'success.main'
                : 'grey.500',
              boxShadow: step.isFailed ? '0 0 0 2px #ffebee' : undefined,
              opacity: step.isPending ? 0.7 : 1,
              overflow: 'hidden',
            }}
          >
            {/* Top row: icon, block name, status/dates/restart */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                p: 2,
                bgcolor: step.isFailed
                  ? '#fff5f5'
                  : step.isDone
                  ? '#f6fbf6'
                  : '#f7f7f7',
              }}
            >
              {/* Left: Icon */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: 36,
                  mr: 2,
                }}
              >
                {statusIcon(
                  step.status || (step.isPending ? 'pending' : 'done')
                )}
              </Box>
              {/* Center: Block name */}
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: 17,
                    color: step.isFailed
                      ? 'error.main'
                      : step.isDone
                      ? 'success.main'
                      : step.isPending
                      ? 'grey.700'
                      : 'inherit',
                  }}
                >
                  {step.block_name}
                </Typography>
              </Box>
              {/* Right: Status, times, restart */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  minWidth: 180,
                  textAlign: 'right',
                }}
              >
                {step.isExecuted && (
                  <>
                    <Typography
                      variant="caption"
                      sx={{
                        color: step.isFailed
                          ? 'error.main'
                          : step.isDone
                          ? 'success.main'
                          : 'grey.600',
                        fontWeight: 500,
                      }}
                    >
                      Status: {step.status}
                    </Typography>
                    {step.started_at && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'inline', ml: 1 }}
                      >
                        Start: {new Date(step.started_at).toLocaleString()}
                      </Typography>
                    )}
                    {step.finished_at && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'inline', ml: 1 }}
                      >
                        Koniec: {new Date(step.finished_at).toLocaleString()}
                      </Typography>
                    )}
                  </>
                )}
                {step.canRestart && (
                  <MuiLink
                    component="button"
                    variant="body2"
                    onClick={() => handleRestartClick(step.block_name)}
                    sx={{
                      ml: 1,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >
                    Uruchom Ponownie
                  </MuiLink>
                )}
              </Box>
            </Box>
            {/* Bottom: log messages and error, full width, no horizontal margin/padding */}
            {Array.isArray(step.log_message) && step.log_message.length > 0 && (
              <Box
                sx={{
                  width: '100%',
                  bgcolor: '#f4f4f4',
                  borderRadius: 0,
                  fontFamily: 'monospace',
                  fontSize: 14,
                  color: 'grey.800',
                  whiteSpace: 'pre-wrap',
                  overflowX: 'auto',
                  px: 0,
                  py: 2,
                  mt: 0,
                }}
              >
                {collapseRepeatedMessages(step.log_message).map(({ msg, count }, idx) => (
                  <Typography
                    key={idx}
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: 14,
                      color: 'grey.800',
                      mb: 0.5,
                      wordBreak: 'break-word',
                      pl: 2,
                    }}
                  >
                    – {msg}{count > 1 ? ` (x${count})` : ''}
                  </Typography>
                ))}
              </Box>
            )}
            {step.isFailed && step.error_message && (
              <Box
                sx={{
                  width: '100%',
                  bgcolor: '#f4f4f4',
                  borderRadius: 0,
                  fontFamily: 'monospace',
                  fontSize: 14,
                  whiteSpace: 'pre-wrap',
                  overflowX: 'auto',
                  px: 0,
                  py: 2,
                  mt: 0,
                }}
              >
                {(() => {
                  try {
                    const errors = JSON.parse(step.error_message);
                    if (Array.isArray(errors)) {
                      return errors.map((error, idx) => (
                        <Typography
                          key={idx}
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: 14,
                            color: 'error.main',
                            mb: 0.5,
                            wordBreak: 'break-word',
                            pl: 2,
                          }}
                        >
                          – {error}
                        </Typography>
                      ));
                    }
                  } catch (e) {
                    // If not JSON, display as plain text
                  }
                  return (
                    <Typography
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: 14,
                        color: 'error.main',
                        mb: 0.5,
                        wordBreak: 'break-word',
                        pl: 2,
                      }}
                    >
                      – {step.error_message}
                    </Typography>
                  );
                })()}
              </Box>
            )}
            {step.async_data && (
              <Box sx={{ px: 2, pt: 1, pb: 0.5, textAlign: 'right' }}>
                <MuiLink
                  component="button"
                  variant="caption"
                  onClick={() => setAsyncDataDialog({ blockName: step.block_name, data: step.async_data })}
                  sx={{ cursor: 'pointer', color: 'text.disabled', fontSize: 11 }}
                >
                  AIConsole log
                </MuiLink>
              </Box>
            )}
          </Box>
        ))}
      </Box>

      {/* Bidder Workflows Section */}
      {bidder_workflows && bidder_workflows.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 2,
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6" gutterBottom>
              Bidder Workflows ({bidder_workflows.length} bidders)
            </Typography>
            <MuiButton
              variant="contained"
              color="secondary"
              onClick={handleProcessBidderWorkflows}
              disabled={processingBidders || record?.status === 'cancelled'}
              startIcon={
                processingBidders ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <PlayArrowIcon />
                )
              }
            >
              Process All Bidders
            </MuiButton>
          </Box>

          {bidder_workflows.map((bidderWorkflow) => (
            <Box
              key={bidderWorkflow.id}
              sx={{
                mb: 3,
                p: 3,
                border: '1px solid',
                borderRadius: 2,
                borderColor: 'grey.300',
                bgcolor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mr: 2 }}>
                    {bidderWorkflow.bidder_name}
                  </Typography>
                  {bidderWorkflow.bidder_offer_id && (
                    <Typography
                      variant="body2"
                      sx={{
                        mr: 2,
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                      }}
                    >
                      (ID: {bidderWorkflow.bidder_offer_id})
                    </Typography>
                  )}
                  <Chip
                    label={statusLabel(bidderWorkflow.status)}
                    color={statusColor(bidderWorkflow.status)}
                    size="small"
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <MuiButton
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      handleProcessBidderWorkflow(
                        bidderWorkflow.bidder_offer_id
                      )
                    }
                    disabled={
                      bidderWorkflow.status === 'done' ||
                      bidderWorkflow.status === 'failed' ||
                      bidderWorkflow.status === 'archived' ||
                      record?.status === 'cancelled'
                    }
                  >
                    Process
                  </MuiButton>
                  <MuiButton
                    variant="outlined"
                    size="small"
                    color="warning"
                    onClick={() =>
                      handleArchiveBidderWorkflow(bidderWorkflow.id)
                    }
                    disabled={bidderWorkflow.status === 'archived'}
                    startIcon={<ArchiveIcon />}
                  >
                    Archive
                  </MuiButton>
                </Box>
              </Box>

              {/* Bidder workflow steps */}
              <Box>
                {bidder_workflow_blocks.map((blockName) => {
                  const step = bidderWorkflow.steps.find(
                    (s) => s.block_name === blockName
                  );
                  const isExecuted = !!step;
                  const isFailed = step?.status === 'failed';
                  const isDone = step?.status === 'done';
                  const isPending = !step;
                  // Create unique ID for this block
                  const blockId = `bidder-workflow-${
                    bidderWorkflow.id
                  }-block-${blockName.replace(/\s+/g, '-').toLowerCase()}`;

                  return (
                    <Box
                      key={blockName}
                      id={blockId}
                      sx={{
                        mb: 2,
                        p: 0,
                        border: '1.5px solid',
                        borderRadius: 2,
                        bgcolor: 'transparent',
                        borderColor: isFailed
                          ? 'error.main'
                          : isDone
                          ? 'success.light'
                          : 'grey.300',
                        color: isFailed
                          ? 'error.main'
                          : isDone
                          ? 'success.main'
                          : 'grey.500',
                        boxShadow: isFailed ? '0 0 0 2px #ffebee' : undefined,
                        opacity: isPending ? 0.7 : 1,
                        overflow: 'hidden',
                      }}
                    >
                      {/* Top row: icon, block name, status/dates/restart */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          p: 2,
                          bgcolor: isFailed
                            ? '#fff5f5'
                            : isDone
                            ? '#f6fbf6'
                            : '#f7f7f7',
                        }}
                      >
                        {/* Left: Icon */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            minWidth: 36,
                            mr: 2,
                          }}
                        >
                          {statusIcon(
                            step?.status || (isPending ? 'pending' : 'done')
                          )}
                        </Box>
                        {/* Center: Block name */}
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: 17,
                              color: isFailed
                                ? 'error.main'
                                : isDone
                                ? 'success.main'
                                : isPending
                                ? 'grey.700'
                                : 'inherit',
                            }}
                          >
                            {blockName}
                          </Typography>
                        </Box>
                        {/* Right: Status, times, restart */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            minWidth: 180,
                            textAlign: 'right',
                          }}
                        >
                          {isExecuted && (
                            <>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: isFailed
                                    ? 'error.main'
                                    : isDone
                                    ? 'success.main'
                                    : 'grey.600',
                                  fontWeight: 500,
                                }}
                              >
                                Status: {step.status}
                              </Typography>
                              {step.started_at && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'inline', ml: 1 }}
                                >
                                  Start:{' '}
                                  {new Date(step.started_at).toLocaleString()}
                                </Typography>
                              )}
                              {step.finished_at && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'inline', ml: 1 }}
                                >
                                  Koniec:{' '}
                                  {new Date(step.finished_at).toLocaleString()}
                                </Typography>
                              )}
                            </>
                          )}
                          {isExecuted && record?.status !== 'cancelled' && (
                            <MuiLink
                              component="button"
                              variant="body2"
                              onClick={() =>
                                handleBidderRestartClick(
                                  bidderWorkflow.id,
                                  blockName
                                )
                              }
                              sx={{
                                ml: 1,
                                cursor: 'pointer',
                                textDecoration: 'underline',
                              }}
                            >
                              Uruchom Ponownie
                            </MuiLink>
                          )}
                        </Box>
                      </Box>
                      {/* Bottom: log messages and error, full width, no horizontal margin/padding */}
                      {Array.isArray(step?.log_message) &&
                        step.log_message.length > 0 && (
                          <Box
                            sx={{
                              width: '100%',
                              bgcolor: '#f4f4f4',
                              borderRadius: 0,
                              fontFamily: 'monospace',
                              fontSize: 14,
                              color: 'grey.800',
                              whiteSpace: 'pre-wrap',
                              overflowX: 'auto',
                              px: 0,
                              py: 2,
                              mt: 0,
                            }}
                          >
                            {step.log_message.map((msg, idx) => (
                              <Typography
                                key={idx}
                                sx={{
                                  fontFamily: 'monospace',
                                  fontSize: 14,
                                  color: 'grey.800',
                                  mb: 0.5,
                                  wordBreak: 'break-word',
                                  pl: 2,
                                }}
                              >
                                – {msg}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      {step && step.error_message && (
                        <Box
                          sx={{
                            width: '100%',
                            bgcolor: '#f4f4f4',
                            borderRadius: 0,
                            fontFamily: 'monospace',
                            fontSize: 14,
                            whiteSpace: 'pre-wrap',
                            overflowX: 'auto',
                            px: 0,
                            py: 2,
                            mt: 0,
                          }}
                        >
                          {(() => {
                            try {
                              const errors = JSON.parse(step.error_message);
                              if (Array.isArray(errors)) {
                                return errors.map((error, idx) => (
                                  <Typography
                                    key={idx}
                                    sx={{
                                      fontFamily: 'monospace',
                                      fontSize: 14,
                                      color: 'error.main',
                                      mb: 0.5,
                                      wordBreak: 'break-word',
                                      pl: 2,
                                    }}
                                  >
                                    – {error}
                                  </Typography>
                                ));
                              }
                            } catch (e) {
                              // If not JSON, display as plain text
                            }
                            return (
                              <Typography
                                sx={{
                                  fontFamily: 'monospace',
                                  fontSize: 14,
                                  color: 'error.main',
                                  mb: 0.5,
                                  wordBreak: 'break-word',
                                  pl: 2,
                                }}
                              >
                                – {step.error_message}
                              </Typography>
                            );
                          })()}
                        </Box>
                      )}
                      {step?.async_data && (
                        <Box sx={{ px: 2, pt: 1, pb: 0.5, textAlign: 'right' }}>
                          <MuiLink
                            component="button"
                            variant="caption"
                            onClick={() => setAsyncDataDialog({ blockName: step.block_name, data: step.async_data })}
                            sx={{ cursor: 'pointer', color: 'text.disabled', fontSize: 11 }}
                          >
                            AIConsole log
                          </MuiLink>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Winner Workflow Section */}
      {winner_workflow && (
        <Box sx={{ mt: 4 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 2,
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6" gutterBottom>
              Winner Workflow
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                label={statusLabel(winner_workflow.status)}
                color={statusColor(winner_workflow.status)}
                sx={{
                  fontWeight: 600,
                  fontSize: 14,
                  height: 28,
                  minWidth: 100,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              />
              <MuiButton
                variant="contained"
                color="primary"
                onClick={handleProcessWinnerWorkflow}
                disabled={
                  winner_workflow.status === 'done' ||
                  winner_workflow.status === 'failed' ||
                  record?.is_archived ||
                  record?.status === 'cancelled'
                }
                startIcon={<PlayArrowIcon />}
              >
                Process Winner Workflow
              </MuiButton>
            </Box>
          </Box>

          <Box
            sx={{
              p: 3,
              border: '1px solid',
              borderRadius: 2,
              borderColor: 'grey.300',
              bgcolor: 'background.paper',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mr: 2 }}>
                  Current Block: {winner_workflow.current_block}
                </Typography>
                <Chip
                  label={statusLabel(winner_workflow.status)}
                  color={statusColor(winner_workflow.status)}
                  size="small"
                />
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                {winner_workflow.next_run_at && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    Scheduled for:{' '}
                    {new Date(winner_workflow.next_run_at).toLocaleString()}
                  </Typography>
                )}
                {winner_workflow.status === 'pending' &&
                  record?.submitting_offers_date && (
                    <Typography variant="body2" color="text.secondary">
                      Auto-start:{' '}
                      {new Date(
                        new Date(record.submitting_offers_date).getTime() +
                          45 * 24 * 60 * 60 * 1000
                      ).toLocaleDateString()}
                    </Typography>
                  )}
              </Box>
            </Box>
            {/* Winner workflow steps - always show all blocks */}
            <Box>
              {winner_workflow_blocks.map((blockName, idx) => {
                const step = winner_workflow.steps.find(
                  (s) => s.block_name === blockName
                );
                const isExecuted = !!step && step.status !== 'pending';
                const isFailed = step?.status === 'failed';
                const isDone = step?.status === 'done';
                const isPending = !step || step.status === 'pending';
                return (
                  <Box
                    key={blockName}
                    sx={{
                      mb: 2,
                      p: 0,
                      border: '1.5px solid',
                      borderRadius: 2,
                      bgcolor: 'transparent',
                      borderColor: isFailed
                        ? 'error.main'
                        : isDone
                        ? 'success.light'
                        : 'grey.300',
                      color: isFailed
                        ? 'error.main'
                        : isDone
                        ? 'success.main'
                        : 'grey.500',
                      boxShadow: isFailed ? '0 0 0 2px #ffebee' : undefined,
                      opacity: isPending ? 0.7 : 1,
                      overflow: 'hidden',
                    }}
                  >
                    {/* Top row: icon, block name, status/dates/restart */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        p: 2,
                        bgcolor: isFailed
                          ? '#fff5f5'
                          : isDone
                          ? '#f6fbf6'
                          : '#f7f7f7',
                      }}
                    >
                      {/* Left: Icon */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 36,
                          mr: 2,
                        }}
                      >
                        {statusIcon(
                          step?.status || (isPending ? 'pending' : 'done')
                        )}
                      </Box>
                      {/* Center: Block name */}
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: 17,
                            color: isFailed
                              ? 'error.main'
                              : isDone
                              ? 'success.main'
                              : isPending
                              ? 'grey.700'
                              : 'inherit',
                          }}
                        >
                          {blockName}
                        </Typography>
                      </Box>
                      {/* Right: Status, times, restart */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          minWidth: 180,
                          textAlign: 'right',
                        }}
                      >
                        {isExecuted && (
                          <>
                            <Typography
                              variant="caption"
                              sx={{
                                color: isFailed
                                  ? 'error.main'
                                  : isDone
                                  ? 'success.main'
                                  : 'grey.600',
                                fontWeight: 500,
                              }}
                            >
                              Status: {step.status}
                            </Typography>
                            {step.started_at && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'inline', ml: 1 }}
                              >
                                Start:{' '}
                                {new Date(step.started_at).toLocaleString()}
                              </Typography>
                            )}
                            {step.finished_at && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'inline', ml: 1 }}
                              >
                                Koniec:{' '}
                                {new Date(step.finished_at).toLocaleString()}
                              </Typography>
                            )}
                          </>
                        )}
                        {isExecuted && record?.status !== 'cancelled' && (
                          <MuiLink
                            component="button"
                            variant="body2"
                            onClick={() => handleWinnerRestartClick(blockName)}
                            sx={{
                              ml: 1,
                              cursor: 'pointer',
                              textDecoration: 'underline',
                            }}
                          >
                            Uruchom Ponownie
                          </MuiLink>
                        )}
                      </Box>
                    </Box>
                    {/* Bottom: log messages and error, full width, no horizontal margin/padding */}
                    {Array.isArray(step?.log_message) &&
                      step.log_message.length > 0 && (
                        <Box
                          sx={{
                            width: '100%',
                            bgcolor: '#f4f4f4',
                            borderRadius: 0,
                            fontFamily: 'monospace',
                            fontSize: 14,
                            color: 'grey.800',
                            whiteSpace: 'pre-wrap',
                            overflowX: 'auto',
                            px: 0,
                            py: 2,
                            mt: 0,
                          }}
                        >
                          {step.log_message.map((msg, idx) => (
                            <Typography
                              key={idx}
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: 14,
                                color: 'grey.800',
                                mb: 0.5,
                                wordBreak: 'break-word',
                                pl: 2,
                              }}
                            >
                              – {msg}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    {step && step.error_message && (
                      <Box
                        sx={{
                          width: '100%',
                          bgcolor: '#f4f4f4',
                          borderRadius: 0,
                          fontFamily: 'monospace',
                          fontSize: 14,
                          whiteSpace: 'pre-wrap',
                          overflowX: 'auto',
                          px: 0,
                          py: 2,
                          mt: 0,
                        }}
                      >
                        {(() => {
                          try {
                            const errors = JSON.parse(step.error_message);
                            if (Array.isArray(errors)) {
                              return errors.map((error, idx) => (
                                <Typography
                                  key={idx}
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: 14,
                                    color: 'error.main',
                                    mb: 0.5,
                                    wordBreak: 'break-word',
                                    pl: 2,
                                  }}
                                >
                                  – {error}
                                </Typography>
                              ));
                            }
                          } catch (e) {
                            // If not JSON, display as plain text
                          }
                          return (
                            <Typography
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: 14,
                                color: 'error.main',
                                mb: 0.5,
                                wordBreak: 'break-word',
                                pl: 2,
                              }}
                            >
                              – {step.error_message}
                            </Typography>
                          );
                        })()}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setBlockToRestart(null);
          setBidderRestartData(null);
          setWinnerRestartData(null);
        }}
        PaperProps={{
          sx: {
            backgroundColor: '#fff',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <DialogTitle>Potwierdzenie restartu</DialogTitle>
        <DialogContent>
          <Typography>
            {bidderRestartData
              ? `Czy na pewno chcesz uruchomić ponownie workflow oferenta od kroku "${bidderRestartData.blockName}"? Spowoduje to zresetowanie całego postępu od tego kroku.`
              : winnerRestartData
              ? `Czy na pewno chcesz uruchomić ponownie workflow zwycięzcy od kroku "${winnerRestartData.blockName}"? Spowoduje to zresetowanie całego postępu od tego kroku.`
              : `Czy na pewno chcesz uruchomić ponownie workflow od kroku "${blockToRestart}"? Spowoduje to zresetowanie całego postępu od tego kroku.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton
            onClick={() => {
              setConfirmOpen(false);
              setBlockToRestart(null);
              setBidderRestartData(null);
              setWinnerRestartData(null);
            }}
          >
            Anuluj
          </MuiButton>
          <MuiButton
            onClick={handleConfirmRestart}
            color="primary"
            variant="contained"
          >
            Potwierdź
          </MuiButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!asyncDataDialog}
        onClose={() => setAsyncDataDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Dane AI Console — {asyncDataDialog?.blockName}</span>
          <IconButton size="small" onClick={() => setAsyncDataDialog(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {asyncDataDialog?.data && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Request</Typography>
              <Box
                component="pre"
                sx={{ bgcolor: '#f4f4f4', borderRadius: 1, p: 2, fontSize: 12, fontFamily: 'monospace', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', mb: 2 }}
              >
                {JSON.stringify(asyncDataDialog.data.request_data, null, 2)}
              </Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Response</Typography>
              <Box
                component="pre"
                sx={{ bgcolor: '#f4f4f4', borderRadius: 1, p: 2, fontSize: 12, fontFamily: 'monospace', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
              >
                {JSON.stringify(asyncDataDialog.data.response_data, null, 2)}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const ArchiveButton = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const [showConfirm, setShowConfirm] = React.useState(false);

  if (!record) return null;

  const handleClick = () => {
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    try {
      if (record.is_archived) {
        await dataProvider.unarchive('tenders', { id: record.id });
        notify('Przetarg został przywrócony', { type: 'success' });
      } else {
        await dataProvider.archive('tenders', { id: record.id });
        notify('Przetarg został zarchiwizowany', { type: 'success' });
      }
      redirect('list', 'tenders');
    } catch (error) {
      notify('Wystąpił błąd', { type: 'error' });
    }
    setShowConfirm(false);
  };

  const handleCancel = () => {
    setShowConfirm(false);
  };

  return (
    <>
      <RaButton
        onClick={handleClick}
        label={record.is_archived ? 'Przywróć' : 'Archiwizuj'}
        startIcon={record.is_archived ? <UnarchiveIcon /> : <ArchiveIcon />}
      />
      <Dialog
        open={showConfirm}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#fff',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <DialogTitle
          sx={{
            padding: '0 0 16px 0',
            color: '#333333',
            fontSize: '1.25rem',
            fontWeight: 500,
          }}
        >
          {record.is_archived
            ? 'Przywracanie przetargu'
            : 'Archiwizacja przetargu'}
        </DialogTitle>
        <DialogContent sx={{ padding: '0 0 24px 0', color: '#666666' }}>
          <Typography>
            {record.is_archived
              ? 'Czy na pewno chcesz przywrócić ten przetarg?'
              : 'Czy na pewno chcesz zarchiwizować ten przetarg?'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: 0 }}>
          <MuiButton
            onClick={handleCancel}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Anuluj
          </MuiButton>
          <MuiButton
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Potwierdź
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

const TenderShowActions = () => {
  const translate = useTranslate();

  return (
    <TopToolbar>
      <EditButton />
      <ListButton />
      <ArchiveButton />
    </TopToolbar>
  );
};

export const TenderShow = (props) => {
  const translate = useTranslate();
  const record = useRecordContext();
  const [searchParams] = useSearchParams();

  return (
    <Show {...props} actions={<TenderShowActions />}>
      <TabbedShowLayout>
        <Tab label={translate('resources.tenders.tabs.basic_info')}>
          <Grid container spacing={4}>
            {/* Tender Details Column */}
            <Grid item xs={12} md={6}>
              <Box sx={{ maxWidth: 700, mt: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {translate(
                    'resources.tenders.sections.basic_info',
                    'Informacje podstawowe'
                  )}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={1}>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.tenders.fields.id')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="id" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.tenders.fields.bzp_number')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="bzp_number" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.tenders.fields.notice_number')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="notice_number" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.tenders.fields.title')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="title" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.tenders.fields.source')}
                  </Grid>
                  <Grid item xs={7}>
                    <FunctionField
                      render={(record) =>
                        record?.source === 'e-Zamowienia' &&
                        record?.external_id ? (
                          <>
                            {record.source}{' '}
                            <a
                              href={`https://ezamowienia.gov.pl/mp-client/search/list/${record.external_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                marginLeft: 8,
                                color: '#1976d2',
                                textDecoration: 'underline',
                              }}
                            >
                              [Source]
                            </a>
                          </>
                        ) : (
                          record?.source || '-'
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate(
                      'resources.tenders.fields.tender_url',
                      'URL postępowania'
                    )}
                  </Grid>
                  <Grid item xs={7}>
                    <FunctionField
                      render={(record) =>
                        record?.details?.tender_url ? (
                          <a
                            href={record.details.tender_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: '#1976d2',
                              textDecoration: 'underline',
                            }}
                          >
                            {record.details.tender_url}
                          </a>
                        ) : (
                          '-'
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.tenders.fields.external_id')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="external_id" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.tenders.fields.cpv_code')}
                  </Grid>
                  <Grid item xs={7}>
                    <FunctionField
                      render={(record) => record?.details?.cpv_code || '-'}
                    />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.tenders.fields.procedure_result')}
                  </Grid>
                  <Grid item xs={7}>
                    <FunctionField
                      render={(record) => {
                        const result = record?.procedure_result;
                        if (!result || result.trim() === '') {
                          return (
                            <Chip
                              label="Brak danych"
                              color="default"
                              size="small"
                            />
                          );
                        }
                        return result;
                      }}
                    />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.tenders.fields.status')}
                  </Grid>
                  <Grid item xs={7}>
                    <FunctionField
                      render={(record) => {
                        const status = record?.status;
                        if (!status)
                          return (
                            <Chip label="-" color="default" size="small" />
                          );

                        let color = 'default';
                        let label = status;

                        switch (status) {
                          case 'not_started':
                            color = 'default';
                            label = 'Nie rozpoczęto';
                            break;
                          case 'in_progress':
                            color = 'warning';
                            label = 'W trakcie';
                            break;
                          case 'completed':
                            color = 'success';
                            label = 'Zakończono';
                            break;
                          case 'failed':
                            color = 'error';
                            label = 'Błąd';
                            break;
                          case 'bidders_completed':
                            color = 'info';
                            label = 'Oferenci zakończeni';
                            break;
                          case 'cancelled':
                            color = 'error';
                            label = 'Anulowano';
                            break;
                          default:
                            color = 'default';
                            label = status;
                        }

                        return (
                          <Chip label={label} color={color} size="small" />
                        );
                      }}
                    />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.tenders.fields.is_archived')}
                  </Grid>
                  <Grid item xs={7}>
                    <FunctionField
                      render={(record) => {
                        const isArchived = record?.is_archived;
                        if (isArchived) {
                          return (
                            <Chip label="Tak" color="default" size="small" />
                          );
                        } else {
                          return (
                            <Chip label="Nie" color="success" size="small" />
                          );
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.tenders.fields.publication_date')}
                  </Grid>
                  <Grid item xs={7}>
                    <DateField source="publication_date" showTime />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate(
                      'resources.tenders.fields.submitting_offers_date'
                    )}
                  </Grid>
                  <Grid item xs={7}>
                    <DateField source="submitting_offers_date" showTime />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.tenders.fields.tender_amount')}
                  </Grid>
                  <Grid item xs={7}>
                    <NumberField
                      source="details.tender_amount"
                      options={{
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }}
                    />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.tenders.fields.procedure_type')}
                  </Grid>
                  <Grid item xs={7}>
                    <FunctionField
                      render={(record) => {
                        const procedura = record?.details?.procedura;
                        if (!procedura)
                          return (
                            <Chip label="-" color="default" size="small" />
                          );
                        if (procedura === 'Krajowa')
                          return (
                            <Chip label="Krajowa" color="info" size="small" />
                          );
                        if (procedura === 'Unijna')
                          return (
                            <Chip label="Unijna" color="primary" size="small" />
                          );
                        return (
                          <Chip
                            label={procedura}
                            color="default"
                            size="small"
                          />
                        );
                      }}
                    />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.tenders.fields.bid_bond')}
                  </Grid>
                  <Grid item xs={7}>
                    <FunctionField
                      render={(record) => {
                        const value = record?.details?.bid_bond;
                        if (value === null || value === undefined)
                          return (
                            <Chip label="-" color="default" size="small" />
                          );
                        if (Number(value) === 0)
                          return (
                            <Chip
                              label={translate('ra.boolean.false')}
                              color="default"
                              size="small"
                            />
                          );
                        if (Number(value) > 0)
                          return (
                            <Chip
                              label={translate('ra.boolean.true')}
                              color="success"
                              size="small"
                            />
                          );
                        return <Chip label="-" color="default" size="small" />;
                      }}
                    />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.tenders.fields.nwu')}
                  </Grid>
                  <Grid item xs={7}>
                    <FunctionField
                      render={(record) => {
                        const value = record?.details?.nwu;
                        if (value === null || value === undefined)
                          return (
                            <Chip label="-" color="default" size="small" />
                          );
                        if (Number(value) === 0)
                          return (
                            <Chip
                              label={translate('ra.boolean.false')}
                              color="default"
                              size="small"
                            />
                          );
                        if (Number(value) > 0)
                          return (
                            <Chip
                              label={translate('ra.boolean.true')}
                              color="success"
                              size="small"
                            />
                          );
                        return <Chip label="-" color="default" size="small" />;
                      }}
                    />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.tenders.fields.created_at')}
                  </Grid>
                  <Grid item xs={7}>
                    <DateField source="created_at" showTime />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.tenders.fields.updated_at')}
                  </Grid>
                  <Grid item xs={7}>
                    <DateField source="updated_at" showTime />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
            {/* Buyer Details Column */}
            <Grid item xs={12} md={6}>
              <Box sx={{ maxWidth: 700, mt: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {translate(
                    'resources.tenders.sections.buyer_info',
                    'Dane zamawiającego'
                  )}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={1}>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.name')}
                  </Grid>
                  <Grid item xs={7}>
                    <FunctionField
                      render={(record) =>
                        record?.ordering_party?.id &&
                        record?.ordering_party?.name ? (
                          <Link
                            to={`/ordering-parties/${record.ordering_party.id}/show`}
                            style={{
                              color: '#1976d2',
                              textDecoration: 'underline',
                            }}
                          >
                            {record.ordering_party.name}
                          </Link>
                        ) : (
                          '-'
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.tax_number')}
                  </Grid>
                  <Grid item xs={7}>
                    <FunctionField
                      render={(record) =>
                        record?.ordering_party?.tax_number || '-'
                      }
                    />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.email')}
                  </Grid>
                  <Grid item xs={7}>
                    <FunctionField
                      render={(record) => record?.ordering_party?.email || '-'}
                    />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.street')}
                  </Grid>
                  <Grid item xs={7}>
                    <FunctionField
                      render={(record) => record?.ordering_party?.street || '-'}
                    />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.city')}
                  </Grid>
                  <Grid item xs={7}>
                    <FunctionField
                      render={(record) => record?.ordering_party?.city || '-'}
                    />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.postal_code')}
                  </Grid>
                  <Grid item xs={7}>
                    <FunctionField
                      render={(record) =>
                        record?.ordering_party?.postal_code || '-'
                      }
                    />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.province')}
                  </Grid>
                  <Grid item xs={7}>
                    <FunctionField
                      render={(record) =>
                        record?.ordering_party?.province || '-'
                      }
                    />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.country')}
                  </Grid>
                  <Grid item xs={7}>
                    <FunctionField
                      render={(record) =>
                        record?.ordering_party?.country || '-'
                      }
                    />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.nuts3_code')}
                  </Grid>
                  <Grid item xs={7}>
                    <FunctionField
                      render={(record) =>
                        record?.ordering_party?.nuts3_name || '-'
                      }
                    />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.party_type')}
                  </Grid>
                  <Grid item xs={7}>
                    <FunctionField
                      render={(record) =>
                        record?.ordering_party?.party_type || '-'
                      }
                    />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.business_activity')}
                  </Grid>
                  <Grid item xs={7}>
                    <FunctionField
                      render={(record) =>
                        record?.ordering_party?.business_activity || '-'
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Tab>
        <Tab label={translate('resources.tenders.tabs.preview')}>
          <TenderPreview />
        </Tab>
        <Tab label={translate('resources.tenders.tabs.updates')}>
          <TenderUpdates />
        </Tab>
        <Tab label={translate('resources.tenders.tabs.bidders')}>
          <TenderBidders />
        </Tab>
        <Tab
          label={translate('resources.tenders.tabs.workflow')}
          path="workflow"
        >
          <WorkflowProgress />
        </Tab>
        <Tab label={translate('resources.tenders.tabs.predictions')}>
          <TenderPredictions />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};

export const TenderEdit = (props) => {
  const translate = useTranslate();
  return (
    <Edit {...props}>
      <StyledSimpleForm>
        {/* Basic Information */}
        <TextInput
          disabled
          source="id"
          label={translate('resources.tenders.fields.id')}
        />
        <TextInput
          source="source"
          label={translate('resources.tenders.fields.source')}
        />
        <TextInput
          source="external_id"
          label={translate('resources.tenders.fields.external_id')}
        />
        <TextInput
          source="tender_id"
          label={translate('resources.tenders.fields.tender_id')}
        />
        <TextInput
          source="bzp_number"
          label={translate('resources.tenders.fields.bzp_number')}
        />
        <TextInput
          source="notice_number"
          label={translate('resources.tenders.fields.notice_number')}
        />
        <TextInput
          source="title"
          label={translate('resources.tenders.fields.title')}
        />

        {/* Dates */}
        <DateTimeInput
          source="publication_date"
          label={translate('resources.tenders.fields.publication_date')}
        />
        <DateTimeInput
          source="submitting_offers_date"
          label={translate('resources.tenders.fields.submitting_offers_date')}
        />

        {/* Additional Fields */}
        <TextInput
          source="procedure_result"
          label={translate('resources.tenders.fields.procedure_result')}
        />
        <BooleanInput
          source="is_archived"
          label={translate('resources.tenders.fields.is_archived')}
        />
        <SelectInput
          source="status"
          choices={[
            { id: 'not_started', name: 'Nie rozpoczęty' },
            { id: 'in_progress', name: 'W toku' },
            { id: 'completed', name: 'Zakończony' },
            { id: 'failed', name: 'Błąd' },
            { id: 'bidders_completed', name: 'Oferenci zakończeni' },
          ]}
          label={translate('resources.tenders.fields.status')}
        />
      </StyledSimpleForm>
    </Edit>
  );
};

export const TenderCreate = (props) => {
  const translate = useTranslate();
  return (
    <Create {...props}>
      <StyledSimpleForm>
        <TextInput
          source="source"
          label={translate('resources.tenders.fields.source')}
        />
        <TextInput
          source="external_reference"
          label={translate('resources.tenders.fields.external_reference')}
        />
        <TextInput
          source="external_id"
          label={translate('resources.tenders.fields.external_id')}
        />
        <TextInput
          source="bzp_number"
          label={translate('resources.tenders.fields.bzp_number')}
        />
        <DateTimeInput
          source="published_at"
          label={translate('resources.tenders.fields.published_at')}
        />
        <DateTimeInput
          source="publication_date"
          label={translate('resources.tenders.fields.publication_date')}
        />
        <TextInput
          source="title"
          label={translate('resources.tenders.fields.title')}
        />
      </StyledSimpleForm>
    </Create>
  );
};

const OrderingPartyLinkField = (props) => {
  const record = useRecordContext(props);
  const translate = useTranslate();
  const party = record?.ordering_party;
  if (!party || !party.id || !party.name) return null;

  return (
    <div className="RaLabeled-root RaField-root" style={{ marginBottom: 16 }}>
      <div
        className="RaLabeled-label"
        style={{ fontWeight: 500, marginBottom: 4 }}
      >
        {translate('resources.buyers.name')}
      </div>
      <div className="RaLabeled-value">
        <a
          href={`/ordering-parties/${party.id}/show`}
          style={{
            color: '#1976d2',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          {party.name}
        </a>
      </div>
    </div>
  );
};

export const PostFilterSidebar = () => {
  const translate = useTranslate();
  const location = useLocation();
  const { setFilters, filterValues } = useListContext();

  // Debug: log filter values to see what's being passed
  console.log('PostFilterSidebar filterValues:', filterValues);

  // Read URL parameters and set filters (similar to bidderOfferLeads)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlFilters = {};
    let hasUrlFilters = false;

    const publicationDateGte = searchParams.get('publication_date_gte');
    if (publicationDateGte) {
      urlFilters.publication_date_gte = publicationDateGte;
      hasUrlFilters = true;
    }

    const publicationDateLte = searchParams.get('publication_date_lte');
    if (publicationDateLte) {
      urlFilters.publication_date_lte = publicationDateLte;
      hasUrlFilters = true;
    }

    const isNwu = searchParams.get('is_nwu');
    if (isNwu) {
      urlFilters.is_nwu = isNwu;
      hasUrlFilters = true;
    }

    const isWadium = searchParams.get('is_wadium');
    if (isWadium) {
      urlFilters.is_wadium = isWadium;
      hasUrlFilters = true;
    }

    if (hasUrlFilters) {
      const currentFilters = { ...filterValues };
      Object.keys(urlFilters).forEach(key => {
        currentFilters[key] = urlFilters[key];
      });
      Object.keys(currentFilters).forEach(key => {
        if (!urlFilters.hasOwnProperty(key) && 
            (key === 'publication_date_gte' || key === 'publication_date_lte' || key === 'is_nwu' || key === 'is_wadium')) {
          delete currentFilters[key];
        }
      });
      setFilters(currentFilters, {});
    }
  }, [location.search, setFilters]);

  // Force empty dates by ignoring filterValues for date fields
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [submissionFrom, setSubmissionFrom] = useState('');
  const [submissionTo, setSubmissionTo] = useState('');
  const [cpv, setCpv] = useState(filterValues.cpv_code || '');
  const [nwu, setNwu] = useState(filterValues.is_nwu || '');
  const [wadium, setWadium] = useState(filterValues.is_wadium || '');
  const [status, setStatus] = useState(filterValues.status || '');

  const handleDateChange = (type, value) => {
    const newFilters = { ...filterValues };

    if (type === 'from') {
      setFrom(value);
      if (value === '') {
        delete newFilters.publication_date_gte;
      } else {
        const [year, month, day] = value.split('-').map(Number);
        const date = new Date(year, month - 1, day, 0, 0, 0, 0);
        newFilters.publication_date_gte = date.toISOString();
      }
    }

    if (type === 'to') {
      setTo(value);
      if (value === '') {
        delete newFilters.publication_date_lte;
      } else {
        const [year, month, day] = value.split('-').map(Number);
        const date = new Date(year, month - 1, day, 23, 59, 59, 999);
        newFilters.publication_date_lte = date.toISOString();
      }
    }

    setFilters(newFilters, {});
  };

  const handleSubmissionDateChange = (type, value) => {
    const newFilters = { ...filterValues };

    if (type === 'from') {
      setSubmissionFrom(value);
      if (value === '') {
        delete newFilters.submitting_offers_date_gte;
      } else {
        newFilters.submitting_offers_date_gte = value;
      }
    }

    if (type === 'to') {
      setSubmissionTo(value);
      if (value === '') {
        delete newFilters.submitting_offers_date_lte;
      } else {
        newFilters.submitting_offers_date_lte = value;
      }
    }

    setFilters(newFilters, {});
  };

  const handleCpvChange = (e) => {
    const value = e.target.value;
    setCpv(value);

    const newFilters = { ...filterValues };
    if (value === '') {
      delete newFilters.cpv_code;
    } else {
      newFilters.cpv_code = value;
    }
    setFilters(newFilters, {});
  };

  const handleNwuChange = (e) => {
    const value = e.target.value;
    setNwu(value);

    const newFilters = { ...filterValues };
    if (value === '') {
      delete newFilters.is_nwu;
    } else {
      newFilters.is_nwu = value;
    }
    setFilters(newFilters, {});
  };

  const handleWadiumChange = (e) => {
    const value = e.target.value;
    setWadium(value);

    const newFilters = { ...filterValues };
    if (value === '') {
      delete newFilters.is_wadium;
    } else {
      newFilters.is_wadium = value;
    }
    setFilters(newFilters, {});
  };

  // Sync state with filterValues for nwu/wadium
  useEffect(() => {
    if (filterValues.is_nwu !== undefined && filterValues.is_nwu !== null) {
      setNwu(filterValues.is_nwu === true || filterValues.is_nwu === 'true' ? 'true' : '');
    } else {
      setNwu('');
    }
  }, [filterValues.is_nwu]);

  useEffect(() => {
    if (filterValues.is_wadium !== undefined && filterValues.is_wadium !== null) {
      setWadium(filterValues.is_wadium === true || filterValues.is_wadium === 'true' ? 'true' : '');
    } else {
      setWadium('');
    }
  }, [filterValues.is_wadium]);

  // Sync state with filterValues for dates (convert ISO to YYYY-MM-DD)
  useEffect(() => {
    if (filterValues.publication_date_gte) {
      try {
        const date = new Date(filterValues.publication_date_gte);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        setFrom(`${year}-${month}-${day}`);
      } catch (e) {
        // If it's already in YYYY-MM-DD format, use it directly
        if (typeof filterValues.publication_date_gte === 'string' && filterValues.publication_date_gte.match(/^\d{4}-\d{2}-\d{2}$/)) {
          setFrom(filterValues.publication_date_gte);
        } else {
          setFrom('');
        }
      }
    } else {
      setFrom('');
    }

    if (filterValues.publication_date_lte) {
      try {
        const date = new Date(filterValues.publication_date_lte);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        setTo(`${year}-${month}-${day}`);
      } catch (e) {
        // If it's already in YYYY-MM-DD format, use it directly
        if (typeof filterValues.publication_date_lte === 'string' && filterValues.publication_date_lte.match(/^\d{4}-\d{2}-\d{2}$/)) {
          setTo(filterValues.publication_date_lte);
        } else {
          setTo('');
        }
      }
    } else {
      setTo('');
    }
  }, [filterValues.publication_date_gte, filterValues.publication_date_lte]);

  const handleStatusChange = (e) => {
    const value = e.target.value;
    setStatus(value);

    const newFilters = { ...filterValues };
    if (value === '') {
      delete newFilters.status;
    } else {
      newFilters.status = value;
    }
    setFilters(newFilters, {});
  };

  const handleResetFilters = () => {
    // Reset all local state
    setFrom('');
    setTo('');
    setSubmissionFrom('');
    setSubmissionTo('');
    setCpv('');
    setNwu('');
    setWadium('');
    setStatus('');

    // Reset filters but keep the required ones (is_archived, status_not)
    const resetFilters = {
      is_archived: filterValues.is_archived,
      status_not: filterValues.status_not,
      status: filterValues.status, // Keep status for specific lists (failed, processed)
    };

    // Remove undefined values
    Object.keys(resetFilters).forEach((key) => {
      if (resetFilters[key] === undefined) {
        delete resetFilters[key];
      }
    });

    setFilters(resetFilters, {});
  };

  return (
    <Card
      sx={{
        order: -1,
        mr: 2,
        mt: 9,
        minWidth: 350,
        maxWidth: 400,
        boxShadow: 0,
        borderRadius: 2,
      }}
    >
      <CardContent>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <FilterLiveSearch />

          <div
            style={{
              marginTop: 16,
              marginBottom: 8,
              fontWeight: 500,
              fontSize: 14,
            }}
          >
            {translate('resources.tenders.fields.publication_date')}
          </div>
          <MuiTextField
            label={translate('resources.tenders.fields.publication_date_from')}
            type="date"
            value={from}
            onChange={(e) => handleDateChange('from', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
            sx={{ mb: 1 }}
          />
          <MuiTextField
            label={translate('resources.tenders.fields.publication_date_to')}
            type="date"
            value={to}
            onChange={(e) => handleDateChange('to', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
            sx={{ mb: 2 }}
          />

          <div
            style={{
              marginTop: 16,
              marginBottom: 8,
              fontWeight: 500,
              fontSize: 14,
            }}
          >
            {translate('resources.tenders.fields.submission_date')}
          </div>
          <MuiTextField
            label={translate('resources.tenders.fields.submission_date_from')}
            type="date"
            value={submissionFrom}
            onChange={(e) => handleSubmissionDateChange('from', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
            sx={{ mb: 1 }}
          />
          <MuiTextField
            label={translate('resources.tenders.fields.submission_date_to')}
            type="date"
            value={submissionTo}
            onChange={(e) => handleSubmissionDateChange('to', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
            sx={{ mb: 2 }}
          />
          <MuiTextField
            label={translate('resources.tenders.fields.cpv_code')}
            value={cpv}
            onChange={handleCpvChange}
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
            sx={{ mb: 2 }}
          />
          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <FormLabel component="legend" sx={{ fontSize: 14 }}>
              {translate('resources.tenders.fields.is_nwu')}
            </FormLabel>
            <RadioGroup row value={nwu} onChange={handleNwuChange}>
              <FormControlLabel
                value=""
                control={<Radio size="small" />}
                label="Wszystkie"
              />
              <FormControlLabel
                value="true"
                control={<Radio size="small" />}
                label={translate('ra.boolean.true')}
              />
              <FormControlLabel
                value="false"
                control={<Radio size="small" />}
                label={translate('ra.boolean.false')}
              />
            </RadioGroup>
          </FormControl>
          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <FormLabel component="legend" sx={{ fontSize: 14 }}>
              {translate('resources.tenders.fields.is_wadium')}
            </FormLabel>
            <RadioGroup row value={wadium} onChange={handleWadiumChange}>
              <FormControlLabel
                value=""
                control={<Radio size="small" />}
                label="Wszystkie"
              />
              <FormControlLabel
                value="true"
                control={<Radio size="small" />}
                label={translate('ra.boolean.true')}
              />
              <FormControlLabel
                value="false"
                control={<Radio size="small" />}
                label={translate('ra.boolean.false')}
              />
            </RadioGroup>
          </FormControl>
          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <FormLabel component="legend" sx={{ fontSize: 14 }}>
              {translate('resources.tenders.fields.status')}
            </FormLabel>
            <RadioGroup value={status} onChange={handleStatusChange}>
              <FormControlLabel
                value=""
                control={<Radio size="small" />}
                label="Wszystkie"
              />
              <FormControlLabel
                value="not_started"
                control={<Radio size="small" />}
                label="Nie rozpoczęty"
              />
              <FormControlLabel
                value="in_progress"
                control={<Radio size="small" />}
                label="W trakcie"
              />
              <FormControlLabel
                value="bidders_completed"
                control={<Radio size="small" />}
                label="Oferenci zakończeni"
              />
              <FormControlLabel
                value="completed"
                control={<Radio size="small" />}
                label="Zakończony"
              />
              <FormControlLabel
                value="failed"
                control={<Radio size="small" />}
                label="Nieudany"
              />
            </RadioGroup>
          </FormControl>
          <FilterList label={translate('resources.tenders.fields.source')}>
            <FilterListItem
              label="e-Zamowienia"
              value={{ source: 'e-Zamowienia' }}
            />
            <FilterListItem label="TED" value={{ source: 'TED' }} />
          </FilterList>

          {/* Reset button at the bottom */}
          <MuiButton
            variant="outlined"
            size="small"
            onClick={handleResetFilters}
            sx={{ mt: 3, mb: 1 }}
            fullWidth
          >
            Wyczyść filtry
          </MuiButton>
        </div>
      </CardContent>
    </Card>
  );
};

// Custom CSV export button that fetches all data and flattens it properly
const ExportCSVButton = () => {
  const { filterValues, sort } = useListContext();
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const translate = useTranslate();

  const handleExport = async () => {
    setLoading(true);
    try {
      // Build query parameters to match current filters and sorting
      const params = new URLSearchParams();

      // Add filters
      Object.entries(filterValues || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });

      // Add sorting
      if (sort?.field) {
        params.append('_sort', sort.field);
        params.append('_order', sort.order?.toLowerCase() || 'desc');
      }

      // Make direct API call to export endpoint
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await fetch(`${apiUrl}/tenders/export/?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        // Display error message from backend if available
        const errorMessage = result.error || 'Export failed';
        notify(errorMessage, { type: 'error' });
        return;
      }
      
      const data = result.data;

      if (!data || data.length === 0) {
        notify(translate('resources.tenders.export.no_data'), {
          type: 'warning',
        });
        return;
      }
      
      // Display warning if export was limited
      if (result.warning) {
        notify(result.warning, { type: 'warning' });
      }
      
      // Flatten the data properly for CSV
      const flattenedData = data.map((record) => ({
        id: record.id || '',
        external_id: record.external_id || '',
        tender_id: record.tender_id || '',
        bzp_number: record.bzp_number || '',
        title: (record.title || '').replace(/"/g, '""'), // Escape quotes
        source: record.source || '',
        publication_date: record.publication_date || '',
        submitting_offers_date: record.submitting_offers_date || '',
        procedure_result: record.procedure_result || '',
        notice_number: record.notice_number || '',
        status: record.status || '',
        is_archived: record.is_archived ? 'Yes' : 'No',
        created_at: record.created_at || '',
        updated_at: record.updated_at || '',
        // Flatten details
        cpv_code: record.details?.cpv_code || '',
        bid_bond: record.details?.bid_bond || '',
        nwu: record.details?.nwu || '',
        tender_url: record.details?.tender_url || '',
        procedura: record.details?.procedura || '',
        tender_amount: record.details?.tender_amount || '',
        // Flatten ordering_party
        ordering_party_name: record.ordering_party?.name || '',
        ordering_party_city: record.ordering_party?.city || '',
        ordering_party_province: record.ordering_party?.province || '',
        ordering_party_country: record.ordering_party?.country || '',
        ordering_party_tax_number: record.ordering_party?.tax_number || '',
        ordering_party_registration_number:
          record.ordering_party?.registration_number || '',
      }));

      // Create CSV manually to ensure proper formatting
      const headers = [
        'ID',
        'External ID',
        'Tender ID',
        'BZP Number',
        'Title',
        'Source',
        'Publication Date',
        'Submitting Offers Date',
        'Procedure Result',
        'Notice Number',
        'Status',
        'Is Archived',
        'Created At',
        'Updated At',
        'CPV Code',
        'Bid Bond',
        'NWU',
        'Tender URL',
        'Procedura',
        'Tender Amount',
        'Ordering Party Name',
        'Ordering Party City',
        'Ordering Party Province',
        'Ordering Party Country',
        'Ordering Party Tax Number',
        'Ordering Party Registration Number',
      ];

      const csvContent = [
        headers.join(','),
        ...flattenedData.map((row) =>
          [
            row.id,
            row.external_id,
            row.tender_id,
            row.bzp_number,
            `"${row.title}"`,
            row.source,
            row.publication_date,
            row.submitting_offers_date,
            row.procedure_result,
            row.notice_number,
            row.status,
            row.is_archived,
            row.created_at,
            row.updated_at,
            row.cpv_code,
            row.bid_bond,
            row.nwu,
            row.tender_url,
            row.procedura,
            row.tender_amount,
            row.ordering_party_name,
            row.ordering_party_city,
            row.ordering_party_province,
            row.ordering_party_country,
            row.ordering_party_tax_number,
            row.ordering_party_registration_number,
          ].join(',')
        ),
      ].join('\n');

      // Download the CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `tenders_export_${new Date().toISOString().split('T')[0]}.csv`
      );
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      notify(
        translate('resources.tenders.export.success', { count: data.length }),
        { type: 'success' }
      );
    } catch (error) {
      console.error('Export error:', error);
      notify(translate('resources.tenders.export.error'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <RaButton
      label={
        loading ? translate('resources.tenders.export.exporting') : 'Export CSV'
      }
      onClick={handleExport}
      disabled={loading}
      startIcon={<FileDownloadIcon />}
      sx={{
        backgroundColor: '#E31837',
        color: '#ffffff',
        '&:hover': {
          backgroundColor: '#C41430',
        },
        '& .MuiSvgIcon-root': {
          color: '#ffffff',
        },
      }}
    />
  );
};

const ListActions = ({ filters }) => {
  const [searchValue, setSearchValue] = useState('');
  const translate = useTranslate();
  const { setFilters } = useListContext();

  const handleSearch = () => {
    setFilters({ title: searchValue }, {});
  };

  return (
    <TopToolbar>
      <SelectColumnsButton />
      <ExportCSVButton />
      <ExportExcelButton />
    </TopToolbar>
  );
};

export const TenderList = (props) => {
  const translate = useTranslate();
  return (
    <List
      {...props}
      actions={<ListActions />}
      aside={<PostFilterSidebar />}
      perPage={25}
      sort={{ field: 'created_at', order: 'DESC' }}
      filter={{ is_archived: false, status_not: 'failed' }}
      filterDefaultValues={{
        is_archived: false,
        status_not: 'failed',
      }}
    >
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <DatagridConfigurable
          sx={{ minWidth: 'auto' }}
          rowClick="show"
          bulkActionButton={false}
          omit={[
            'created_at',
            'updated_at',
            'details.cpv_code',
            'procedure_type',
            'notice_type',
            'order_type',
            'buyer_type',
            'buyer_province',
            'is_nwu',
            'is_wadium',
            'submitting_offers_date',
          ]}
        >
          <TextField
            source="id"
            label={translate('resources.tenders.fields.id')}
          />
          <TextField
            source="source"
            label={translate('resources.tenders.fields.source')}
          />
          <TextField
            source="external_id"
            label={translate('resources.tenders.fields.external_id')}
          />
          <TextField
            source="bzp_number"
            label={translate('resources.tenders.fields.bzp_number')}
          />
          <DateField
            source="publication_date"
            label={translate('resources.tenders.fields.publication_date')}
            showTime
          />
          <DateField
            source="submitting_offers_date"
            label={translate('resources.tenders.fields.submitting_offers_date')}
            showTime
          />
          <TextField
            source="title"
            label={translate('resources.tenders.fields.title')}
          />
          <TextField
            source="details.cpv_code"
            label={translate('resources.tenders.fields.cpv_code')}
          />
          <DateField
            source="created_at"
            label={translate('resources.tenders.fields.created_at')}
            showTime
          />
          <DateField
            source="updated_at"
            label={translate('resources.tenders.fields.updated_at')}
            showTime
          />
          <FunctionField
            label={translate('resources.tenders.fields.procedure_type')}
            sortBy="details__procedura"
            render={(record) => {
              const procedura = record?.details?.procedura;
              if (!procedura) return '-';
              if (procedura === 'Krajowa')
                return <Chip label="Krajowa" color="info" size="small" />;
              if (procedura === 'Unijna')
                return <Chip label="Unijna" color="primary" size="small" />;
              return <Chip label={procedura} color="default" size="small" />;
            }}
          />
          <TextField
            source="notice_type"
            label={translate('resources.tenders.fields.notice_type')}
          />
          <TextField
            source="order_type"
            label={translate('resources.tenders.fields.order_type')}
          />
          <TextField
            source="buyer_type"
            label={translate('resources.tenders.fields.buyer_type')}
          />
          <TextField
            source="buyer_province"
            label={translate('resources.tenders.fields.buyer_province')}
          />
          <TextField
            source="buyer_city"
            label={translate('resources.tenders.fields.buyer_city')}
          />
          <TextField
            source="ordering_party.nuts3_name"
            label={translate('resources.tenders.fields.buyer_commune')}
          />
          <FunctionField
            label="Predykcja"
            sortable={false}
            render={(record) => {
              const canPredict = record?.can_predict;
              return (
                <Chip
                  icon={canPredict ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                  label={canPredict ? 'Dostępna' : 'Niedostępna'}
                  color={canPredict ? 'success' : 'default'}
                  size="small"
                  variant={canPredict ? 'filled' : 'outlined'}
                />
              );
            }}
          />
          <WorkflowStatusField
            source="status"
            label={translate('resources.tenders.fields.status')}
          />
          <ArchivedStatusField
            source="is_archived"
            label={translate('resources.tenders.fields.is_archived')}
          />
          <ShowButton label={translate('ra.action.show')} />
        </DatagridConfigurable>
      </div>
    </List>
  );
};

export const ArchivedTenderList = (props) => {
  const translate = useTranslate();
  return (
    <List
      resource="tenders/archived"
      {...props}
      actions={<ListActions />}
      aside={<PostFilterSidebar />}
      perPage={25}
      sort={{ field: 'created_at', order: 'DESC' }}
    >
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <DatagridConfigurable
          sx={{ minWidth: 'auto' }}
          rowClick="show"
          bulkActionButtons={false}
          omit={[
            'created_at',
            'updated_at',
            'details.cpv_code',
            'procedure_type',
            'notice_type',
            'order_type',
            'buyer_type',
            'buyer_province',
            'is_nwu',
            'is_wadium',
            'submitting_offers_date',
          ]}
        >
          <TextField
            source="id"
            label={translate('resources.tenders.fields.id')}
          />
          <TextField
            source="source"
            label={translate('resources.tenders.fields.source')}
          />
          <TextField
            source="external_id"
            label={translate('resources.tenders.fields.external_id')}
          />
          <TextField
            source="bzp_number"
            label={translate('resources.tenders.fields.bzp_number')}
          />
          <DateField
            source="publication_date"
            label={translate('resources.tenders.fields.publication_date')}
            showTime
          />
          <DateField
            source="submitting_offers_date"
            label={translate('resources.tenders.fields.submitting_offers_date')}
            showTime
          />
          <TextField
            source="title"
            label={translate('resources.tenders.fields.title')}
          />
          <TextField
            source="details.cpv_code"
            label={translate('resources.tenders.fields.cpv_code')}
          />
          <DateField
            source="created_at"
            label={translate('resources.tenders.fields.created_at')}
            showTime
          />
          <DateField
            source="updated_at"
            label={translate('resources.tenders.fields.updated_at')}
            showTime
          />
          <FunctionField
            label={translate('resources.tenders.fields.procedure_type')}
            sortBy="details__procedura"
            render={(record) => {
              const procedura = record?.details?.procedura;
              if (!procedura) return '-';
              if (procedura === 'Krajowa')
                return <Chip label="Krajowa" color="info" size="small" />;
              if (procedura === 'Unijna')
                return <Chip label="Unijna" color="primary" size="small" />;
              return <Chip label={procedura} color="default" size="small" />;
            }}
          />
          <TextField
            source="notice_type"
            label={translate('resources.tenders.fields.notice_type')}
          />
          <TextField
            source="order_type"
            label={translate('resources.tenders.fields.order_type')}
          />
          <TextField
            source="buyer_type"
            label={translate('resources.tenders.fields.buyer_type')}
          />
          <TextField
            source="buyer_province"
            label={translate('resources.tenders.fields.buyer_province')}
          />
          <TextField
            source="buyer_city"
            label={translate('resources.tenders.fields.buyer_city')}
          />
          <TextField
            source="ordering_party.nuts3_name"
            label={translate('resources.tenders.fields.buyer_commune')}
          />
          <FunctionField
            label="Predykcja"
            sortable={false}
            render={(record) => {
              const canPredict = record?.can_predict;
              return (
                <Chip
                  icon={canPredict ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                  label={canPredict ? 'Dostępna' : 'Niedostępna'}
                  color={canPredict ? 'success' : 'default'}
                  size="small"
                  variant={canPredict ? 'filled' : 'outlined'}
                />
              );
            }}
          />
          <WorkflowStatusField
            source="status"
            label={translate('resources.tenders.fields.status')}
          />
          <ArchivedStatusField
            source="is_archived"
            label={translate('resources.tenders.fields.is_archived')}
          />
          <ShowButton label={translate('ra.action.show')} />
        </DatagridConfigurable>
      </div>
    </List>
  );
};

export const FailedTenderList = (props) => {
  const translate = useTranslate();
  return (
    <List
      resource="tenders/failed"
      {...props}
      actions={<ListActions />}
      aside={<PostFilterSidebar />}
      perPage={25}
      sort={{ field: 'created_at', order: 'DESC' }}
      filter={{ workflow_status: 'failed', is_archived: false }}
      filterDefaultValues={{
        workflow_status: 'failed',
        is_archived: false,
      }}
    >
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <DatagridConfigurable
          sx={{ minWidth: 'auto' }}
          rowClick="show"
          bulkActionButtons={false}
          omit={[
            'created_at',
            'updated_at',
            'details.cpv_code',
            'procedure_type',
            'notice_type',
            'order_type',
            'buyer_type',
            'buyer_province',
            'is_nwu',
            'is_wadium',
            'submitting_offers_date',
          ]}
        >
          <TextField
            source="id"
            label={translate('resources.tenders.fields.id')}
          />
          <TextField
            source="source"
            label={translate('resources.tenders.fields.source')}
          />
          <TextField
            source="external_id"
            label={translate('resources.tenders.fields.external_id')}
          />
          <TextField
            source="bzp_number"
            label={translate('resources.tenders.fields.bzp_number')}
          />
          <DateField
            source="publication_date"
            label={translate('resources.tenders.fields.publication_date')}
            showTime
          />
          <DateField
            source="submitting_offers_date"
            label={translate('resources.tenders.fields.submitting_offers_date')}
            showTime
          />
          <TextField
            source="title"
            label={translate('resources.tenders.fields.title')}
          />
          <TextField
            source="details.cpv_code"
            label={translate('resources.tenders.fields.cpv_code')}
          />
          <DateField
            source="created_at"
            label={translate('resources.tenders.fields.created_at')}
            showTime
          />
          <DateField
            source="updated_at"
            label={translate('resources.tenders.fields.updated_at')}
            showTime
          />
          <FunctionField
            label={translate('resources.tenders.fields.procedure_type')}
            sortBy="details__procedura"
            render={(record) => {
              const procedura = record?.details?.procedura;
              if (!procedura) return '-';
              if (procedura === 'Krajowa')
                return <Chip label="Krajowa" color="info" size="small" />;
              if (procedura === 'Unijna')
                return <Chip label="Unijna" color="primary" size="small" />;
              return <Chip label={procedura} color="default" size="small" />;
            }}
          />
          <TextField
            source="notice_type"
            label={translate('resources.tenders.fields.notice_type')}
          />
          <TextField
            source="order_type"
            label={translate('resources.tenders.fields.order_type')}
          />
          <TextField
            source="buyer_type"
            label={translate('resources.tenders.fields.buyer_type')}
          />
          <TextField
            source="buyer_province"
            label={translate('resources.tenders.fields.buyer_province')}
          />
          <TextField
            source="buyer_city"
            label={translate('resources.tenders.fields.buyer_city')}
          />
          <TextField
            source="ordering_party.nuts3_name"
            label={translate('resources.tenders.fields.buyer_commune')}
          />
          <FunctionField
            label="Predykcja"
            sortable={false}
            render={(record) => {
              const canPredict = record?.can_predict;
              return (
                <Chip
                  icon={canPredict ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                  label={canPredict ? 'Dostępna' : 'Niedostępna'}
                  color={canPredict ? 'success' : 'default'}
                  size="small"
                  variant={canPredict ? 'filled' : 'outlined'}
                />
              );
            }}
          />
          <WorkflowStatusField
            source="status"
            label={translate('resources.tenders.fields.status')}
          />
          <ArchivedStatusField
            source="is_archived"
            label={translate('resources.tenders.fields.is_archived')}
          />
          <ShowButton label={translate('ra.action.show')} />
        </DatagridConfigurable>
      </div>
    </List>
  );
};

export const ProcessedTenderList = (props) => {
  const translate = useTranslate();
  return (
    <List
      resource="tenders/processed"
      {...props}
      actions={<ListActions />}
      aside={<PostFilterSidebar />}
      perPage={25}
      sort={{ field: 'created_at', order: 'DESC' }}
      filter={{ status: 'completed', is_archived: false }}
      filterDefaultValues={{
        status: 'completed',
        is_archived: false,
      }}
    >
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <DatagridConfigurable
          sx={{ minWidth: 'auto' }}
          rowClick="show"
          bulkActionButtons={false}
          omit={[
            'created_at',
            'updated_at',
            'details.cpv_code',
            'procedure_type',
            'notice_type',
            'order_type',
            'buyer_type',
            'buyer_province',
            'is_nwu',
            'is_wadium',
            'submitting_offers_date',
          ]}
        >
          <TextField
            source="id"
            label={translate('resources.tenders.fields.id')}
          />
          <TextField
            source="source"
            label={translate('resources.tenders.fields.source')}
          />
          <TextField
            source="external_id"
            label={translate('resources.tenders.fields.external_id')}
          />
          <TextField
            source="bzp_number"
            label={translate('resources.tenders.fields.bzp_number')}
          />
          <DateField
            source="publication_date"
            label={translate('resources.tenders.fields.publication_date')}
            showTime
          />
          <DateField
            source="submitting_offers_date"
            label={translate('resources.tenders.fields.submitting_offers_date')}
            showTime
          />
          <TextField
            source="title"
            label={translate('resources.tenders.fields.title')}
          />
          <TextField
            source="details.cpv_code"
            label={translate('resources.tenders.fields.cpv_code')}
          />
          <DateField
            source="created_at"
            label={translate('resources.tenders.fields.created_at')}
            showTime
          />
          <DateField
            source="updated_at"
            label={translate('resources.tenders.fields.updated_at')}
            showTime
          />
          <FunctionField
            label={translate('resources.tenders.fields.procedure_type')}
            sortBy="details__procedura"
            render={(record) => {
              const procedura = record?.details?.procedura;
              if (!procedura) return '-';
              if (procedura === 'Krajowa')
                return <Chip label="Krajowa" color="info" size="small" />;
              if (procedura === 'Unijna')
                return <Chip label="Unijna" color="primary" size="small" />;
              return <Chip label={procedura} color="default" size="small" />;
            }}
          />
          <TextField
            source="notice_type"
            label={translate('resources.tenders.fields.notice_type')}
          />
          <TextField
            source="order_type"
            label={translate('resources.tenders.fields.order_type')}
          />
          <TextField
            source="buyer_type"
            label={translate('resources.tenders.fields.buyer_type')}
          />
          <TextField
            source="buyer_province"
            label={translate('resources.tenders.fields.buyer_province')}
          />
          <TextField
            source="buyer_city"
            label={translate('resources.tenders.fields.buyer_city')}
          />
          <TextField
            source="ordering_party.nuts3_name"
            label={translate('resources.tenders.fields.buyer_commune')}
          />
          <FunctionField
            label="Predykcja"
            sortable={false}
            render={(record) => {
              const canPredict = record?.can_predict;
              return (
                <Chip
                  icon={canPredict ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                  label={canPredict ? 'Dostępna' : 'Niedostępna'}
                  color={canPredict ? 'success' : 'default'}
                  size="small"
                  variant={canPredict ? 'filled' : 'outlined'}
                />
              );
            }}
          />
          <WorkflowStatusField
            source="status"
            label={translate('resources.tenders.fields.status')}
          />
          <ArchivedStatusField
            source="is_archived"
            label={translate('resources.tenders.fields.is_archived')}
          />
          <ShowButton label={translate('ra.action.show')} />
        </DatagridConfigurable>
      </div>
    </List>
  );
};

// Workflow Status Field Component
const WorkflowStatusField = ({ source, label }) => {
  const record = useRecordContext();
  const translate = useTranslate();

  if (!record) return null;

  const status = record[source];

  const getStatusConfig = (status) => {
    switch (status) {
      case 'not_started':
        return { color: 'default', label: 'Not Started' };
      case 'in_progress':
        return { color: 'primary', label: 'In Progress' };
      case 'completed':
        return { color: 'success', label: 'Completed' };
      case 'failed':
        return { color: 'error', label: 'Failed' };
      case 'partial':
        return { color: 'warning', label: 'Bidders Completed' };
      case 'bidders_completed':
        return { color: 'warning', label: 'Bidders Completed' };
      case 'cancelled':
        return { color: 'error', label: 'Cancelled' };
      default:
        return { color: 'default', label: status || 'Unknown' };
    }
  };

  const config = getStatusConfig(status);

  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      variant="outlined"
    />
  );
};

// Archived Status Field Component
const ArchivedStatusField = ({ source, label }) => {
  const record = useRecordContext();
  const translate = useTranslate();

  if (!record) return null;

  const isArchived = record[source];

  return (
    <Chip
      label={isArchived ? 'Archived' : 'Active'}
      color={isArchived ? 'default' : 'success'}
      size="small"
      variant="outlined"
    />
  );
};
